import { useState, useEffect, useCallback, useRef, memo, useMemo, forwardRef } from 'react';
import { useParams, Link, useLocation, useNavigate } from 'react-router-dom';
import {
  Button,
  Table,
  TableBody,
  TextField,
  TableCell,
  TableContainer,
  TableHead,
  Stack,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Modal,
  TableRow,
  Paper,
  Typography,
  Box,
  CircularProgress,
  IconButton,
  FormHelperText,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  LinearProgress,
  Tabs,
  Tab,
  Menu,
  Popover,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import * as XLSX from 'xlsx';
import Papa from 'papaparse';
import { toast } from 'react-toastify';
import { ArrowLeft, Download, Edit, Upload, X, Image as ImageIcon, Play, Trash2, CheckCircle, XCircle, RefreshCw, SkipForward, Plus } from 'lucide-react';
import { useDropzone } from 'react-dropzone';
import Pagination from '@mui/material/Pagination';

const URL = process.env.REACT_APP_API_ENDPOINT;

// Common debounce function
const debounce = (func, delay) => {
  let timeoutId;
  return (...args) => {
    clearTimeout(timeoutId);
    timeoutId = setTimeout(() => func(...args), delay);
  };
};

/* eslint-disable no-await-in-loop, no-plusplus, no-loop-func, prefer-exponentiation-operator, no-restricted-properties */
// --- Batch image upload helpers ---
const PRESIGN_ENDPOINT = 'https://cenjhoa9fd.execute-api.ap-south-1.amazonaws.com/presign/batch';
const SAVE_BATCH_ENDPOINT = 'https://cenjhoa9fd.execute-api.ap-south-1.amazonaws.com/uploads/save-batch';
const BATCH_SIZE = 100;
const CONCURRENCY = 6;
const MAX_RETRIES = 3;
const RETRY_BASE_MS = 500;

const chunkArray = (arr, size) => {
  const out = [];
  for (let i = 0; i < arr.length; i += size) out.push(arr.slice(i, i + size));
  return out;
};

const sleep = (ms) => new Promise((res) => setTimeout(res, ms));

// Upload a single file to the presigned upload_url (PUT method, file only in body)
const uploadFileXHR = async (url, file) => {
  const res = await fetch(url, {
    method: 'PUT',
    body: file,
  });
  if (!res.ok) {
    throw new Error(`Upload failed with status ${res.status}`);
  }
  return { status: res.status, statusText: res.statusText };
};

const uploadSingleFileWithRetry = async (fileEntry, presigned) => {
  let attempt = 0;
  while (attempt < MAX_RETRIES) {
    try {
      await uploadFileXHR(presigned.upload_url || presigned.url, fileEntry.file);
      return { ok: true, filename: fileEntry.filename, final_url: presigned.final_url || presigned.url };
    } catch (err) {
      attempt += 1;
      if (attempt >= MAX_RETRIES) {
        return { ok: false, filename: fileEntry.filename, error: err.message || 'Upload failed' };
      }
      await sleep(RETRY_BASE_MS * (2 ** attempt));
    }
  }
  return { ok: false, filename: fileEntry.filename, error: 'Upload failed' };
};

const uploadBatchWithConcurrency = async (entries, presignMap, onProgressUpdate) => {
  const results = [];
  let active = 0;
  let idx = 0;

  return new Promise((resolve) => {
    const next = () => {
      if (idx >= entries.length && active === 0) {
        resolve(results);
        return;
      }
      while (active < CONCURRENCY && idx < entries.length) {
        const entry = entries[idx++];
        active += 1;
        (async () => {
          const presigned = presignMap.get(entry.filename);
          if (!presigned) {
            results.push({ ok: false, filename: entry.filename, error: 'No presigned URL returned' });
            active -= 1;
            next();
            return;
          }
          const res = await uploadSingleFileWithRetry(entry, presigned);
          results.push(res);
          if (onProgressUpdate) onProgressUpdate(res);
          active -= 1;
          next();
        })();
      }
    };
    next();
  });
};
/* eslint-enable no-await-in-loop, no-plusplus, no-loop-func, prefer-exponentiation-operator, no-restricted-properties */

const AdminRunners = () => {
  const location = useLocation();
  const { eventId } = useParams();
  const navigation = useNavigate();
  const eventName = location.state.eventName;
  const isRekla = location.state.isRekla;
  const eventStartTime = location.state.startTime;
  const eventEndTime = location.state.endTime;

  // Memoized event object to prevent unnecessary re-renders
  const event = useMemo(
    () => ({
      name: eventName || '',
      startTime: eventStartTime,
      endTime: eventEndTime,
    }),
    [eventName, eventStartTime, eventEndTime]
  );
  const [runners, setRunners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [cartSearchTerm, setCartSearchTerm] = useState('');
  const [tokenSearchTerm, setTokenSearchTerm] = useState('');

  // Refs to hold current search values without causing re-renders
  const searchTermRef = useRef('');
  const cartSearchTermRef = useRef('');
  const tokenSearchTermRef = useRef('');

  // Refs for input focus management
  const cartSearchInputRef = useRef(null);
  const tokenSearchInputRef = useRef(null);
  const regularSearchInputRef = useRef(null);
  const lastFocusedInputRef = useRef(null);
  const [refresh, setRefresh] = useState(0);
  const token = localStorage.getItem('accessToken');
  const [page, setPage] = useState(1);
  const limit = 10; // fixed limit per your API
  const [totalPages, setTotalPages] = useState(1);

  const [confirmOpen, setConfirmOpen] = useState(false);
  const [pendingBibNumber, setPendingBibNumber] = useState(null);
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [deleteRunnersConfirmOpen, setDeleteRunnersConfirmOpen] = useState(false);
  const [clearImagesConfirmOpen, setClearImagesConfirmOpen] = useState(false);
  const menuButtonRef = useRef(null);
  const [menuPosition, setMenuPosition] = useState({ top: 0, left: 0 });

  // Edit modal state
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editingRunner, setEditingRunner] = useState(null);
  const [editFormData, setEditFormData] = useState({
    StartTime: '',
    EndTime: '',
    Duration: '',
  });
  const [editLoading, setEditLoading] = useState(false);

  // Visitor count state
  const [visitorCount, setVisitorCount] = useState(0);
  const [visitorCountLoading, setVisitorCountLoading] = useState(false);

  // Batch image upload state
  const [imageFiles, setImageFiles] = useState([]);
  const [batches, setBatches] = useState([]);
  const [batchStatus, setBatchStatus] = useState([]);
  const [currentBatchIndex, setCurrentBatchIndex] = useState(0);
  const [batchRunning, setBatchRunning] = useState(false);
  const [batchStats, setBatchStats] = useState({ uploaded: 0, failed: 0, total: 0 });
  const [batchLogs, setBatchLogs] = useState([]);
  const [selectedBatchForView, setSelectedBatchForView] = useState(null);
  const [batchUploadModalOpen, setBatchUploadModalOpen] = useState(false);
  const [batchDetailsTab, setBatchDetailsTab] = useState('uploaded');
  const [showCloseConfirm, setShowCloseConfirm] = useState(false);
  const [failedFilesModalOpen, setFailedFilesModalOpen] = useState(false);
  const [pendingFailedFiles, setPendingFailedFiles] = useState([]);
  const [pendingBatchIndex, setPendingBatchIndex] = useState(null);
  const [batchUploadProgress, setBatchUploadProgress] = useState({}); // Track progress per batch

  const confirmDelete = (bibNumber) => {
    setPendingBibNumber(bibNumber);
    setConfirmOpen(true);
  };

  useEffect(() => {
    document.title = 'Runner Management - Marathon Tracker';
  }, [eventId]);

  // Memoized function to format date for API (YYYY-MM-DD format)
  const formatDateForAPI = useCallback((dateString) => {
    if (!dateString) return null;
    const date = new Date(dateString);
    return date.toISOString().split('T')[0]; // Returns YYYY-MM-DD
  }, []);

  // Memoized function to fetch visitor count
  const fetchVisitorCount = useCallback(async () => {
    if (!event.startTime || !event.endTime) {
      console.warn('Event dates not available for visitor count');
      return;
    }

    setVisitorCountLoading(true);
    try {
      const fromDate = formatDateForAPI(event.startTime);
      const toDate = formatDateForAPI(event.endTime);

      if (!fromDate || !toDate) {
        console.warn('Could not format dates for visitor count API');
        return;
      }

      const response = await fetch(`${URL}Event/GetRaceDayVisitorCount?from_date=${fromDate}&to_date=${toDate}`, {
        method: 'GET',
        headers: {
          accept: 'application/json',
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.status === 403) {
        console.warn('Access forbidden: 403');
        localStorage.setItem('adminAuthenticated', false);
        navigation('/login');
        return;
      }

      const data = await response.json();
      console.log('Visitor count API response:', data);

      if (data?.Status === 1) {
        // Calculate total visitors from all days in the range
        const totalVisitors =
          data.DailyVisitorCounts?.reduce((total, day) => {
            return total + (day.unique_visitors || 0);
          }, 0) || 0;
        setVisitorCount(totalVisitors);
      } else {
        console.error('Unexpected visitor count API response:', data);
      }
    } catch (error) {
      console.error('Failed to fetch visitor count:', error);
    } finally {
      setVisitorCountLoading(false);
    }
  }, [event.startTime, event.endTime, token, navigation]);

  const fetchRunners = useCallback(
    async (search = '', page = 1, searchType = null) => {
      // Only show main loading for initial load, search loading for search operations
      if (search) {
        setSearchLoading(true);
      } else {
        setLoading(true);
      }
      try {
        let searchParam = '';

        if (isRekla) {
          if (searchType === 'token' && search) {
            // For token number search, use BibNumber parameter
            searchParam = `&BibNumber=${search}`;
          } else if (searchType === 'cart' && search) {
            // For cart number search, use SearchTerm and SearchType
            searchParam = `&SearchTerm=${search}&SearchType=cart`;
          }
        } else if (search) {
          // For non-Rekla events, use SearchTerm
          searchParam = `&SearchTerm=${search}`;
        }

        const response = await fetch(
          `${URL}Runner/ListEventRunners?EventInfoKey=${eventId}${searchParam}&page=${page}&limit=${limit}`,
          {
            method: 'GET',
            headers: {
              accept: 'application/json',
              Authorization: `Bearer ${token}`,
            },
          }
        );

        if (response.status === 403) {
          console.warn('Access forbidden: 403');
          localStorage.setItem('adminAuthenticated', false);
          navigation('/login');
          return;
        }

        const data = await response.json();

        if (data?.Status === 1) {
          const transformedRunners = (data.Runners || []).map((runner) => ({
            id: runner.RunnerKey,
            name: runner.RunnerName,
            bibNumber: runner.BibNumber,
            category: runner.Category,
            RunnerKey: runner.RunnerKey,
            StartTime: runner.StartTime,
            EndTime: runner.EndTime,
            Duration: runner.Duration,
          }));
          const transformedReklaRunners = (data.Runners || []).map((runner) => ({
            id: runner.RunnerKey,
            name: runner.RunnerName,
            name2: runner.RunnerName2,
            bibNumber: runner.BibNumber,
            cartNumber: runner.CartNumber,
            category: runner.Category,
            RunnerKey: runner.RunnerKey,
            StartTime: runner.StartTime,
            EndTime: runner.EndTime,
            Duration: runner.Duration,
          }));
          setRunners(isRekla ? transformedReklaRunners : transformedRunners);
          setTotalPages(data?.Pagination.TotalPages || 1); // assuming API returns this
        } else {
          console.error('Unexpected API response:', data);
        }
      } catch (error) {
        console.error('Failed to fetch runners:', error);
      } finally {
        setLoading(false);
        setSearchLoading(false);
      }
    },
    [isRekla, eventId, token, navigation]
  );

  useEffect(() => {
    fetchRunners(searchTerm, page);
  }, [fetchRunners, searchTerm, page, refresh]);

  // Fetch visitor count when component mounts
  useEffect(() => {
    if (event.startTime && event.endTime) {
      fetchVisitorCount();
    }
  }, [fetchVisitorCount, event.startTime, event.endTime]);

  // Keep selectedBatchForView in sync with batchStatus when modal is open
  useEffect(() => {
    if (selectedBatchForView && batchStatus.length > 0) {
      const updatedBatch = batchStatus.find(
        (b) => b.batchNumber === selectedBatchForView.batchNumber
      );
      if (updatedBatch) {
        setSelectedBatchForView(updatedBatch);
      }
    }
  }, [batchStatus, selectedBatchForView?.batchNumber]);

  // Function to restore focus to the last focused input
  const restoreFocus = useCallback(() => {
    if (lastFocusedInputRef.current) {
      setTimeout(() => {
        lastFocusedInputRef.current?.focus();
      }, 100); // Small delay to ensure DOM is updated
    }
  }, []);

  const handleSearch = useCallback(
    (searchValue, searchType) => {
      if (searchValue && searchValue.trim() !== '') {
        setPage(1); // Reset to first page when searching
        fetchRunners(searchValue.trim(), 1, searchType);
      } else {
        // Optionally: Clear the list or fetch all runners again
        setPage(1); // Reset to first page when clearing search
        fetchRunners('', 1);
      }
      // Restore focus after search operation
      restoreFocus();
    },
    [fetchRunners, restoreFocus]
  );

  // Optimized debounce - only updates state when actually executing
  const debouncedCartSearch = useMemo(
    () =>
      debounce((searchValue) => {
        // Update state only when debounced function actually executes
        setCartSearchTerm(searchValue);
        handleSearch(searchValue, 'cart');
      }, 800),
    [handleSearch]
  );

  const debouncedTokenSearch = useMemo(
    () =>
      debounce((searchValue) => {
        // Update state only when debounced function actually executes
        setTokenSearchTerm(searchValue);
        handleSearch(searchValue, 'token');
      }, 800),
    [handleSearch]
  );

  const debouncedRegularSearch = useMemo(
    () =>
      debounce((searchValue) => {
        // Update state only when debounced function actually executes
        setSearchTerm(searchValue);
        handleSearch(searchValue, null);
      }, 800),
    [handleSearch]
  );

  const handleConfirmedDelete = async () => {
    try {
      console.log(eventId, pendingBibNumber);
      const baseUrl = `${URL}LiveTrack/DeleteLiveResults?EventInfoKey=${eventId}`;
      const finalUrl = pendingBibNumber ? `${baseUrl}&RunnerKey=${pendingBibNumber}` : baseUrl;

      const response = await fetch(finalUrl, {
        method: 'DELETE',
        headers: { accept: 'application/json' },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }

      const data = await response.json();
      console.log('Delete successful:', data);
      toast.success('Delete successful');
      // Refresh the runners list while preserving search term and current page
      setRefresh((prev) => prev + 1);
    } catch (error) {
      console.error('Error deleting:', error);
      toast.error('Error deleting');
    } finally {
      setConfirmOpen(false);
      setPendingBibNumber(null);
    }
  };

  const handleDownloadRunners = async () => {
    console.log('Download function called');
    console.log('Event name:', event.name);
    console.log('Is Rekla:', isRekla);

    try {
      setLoading(true);

      // Call the new download API
      const response = await fetch(`${URL}Runner/DownloadEventRunners?EventInfoKey=${eventId}`, {
        method: 'GET',
        headers: {
          accept: 'application/json',
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.status === 403) {
        console.warn('Access forbidden: 403');
        localStorage.setItem('adminAuthenticated', false);
        navigation('/login');
        return;
      }

      const data = await response.json();
      console.log('Download API response:', data);

      if (data?.Status !== 1) {
        toast.error('Failed to fetch runners data for download');
        return;
      }

      // Process the runners data from both 200m and 300m categories
      const runners200m = data.Runners?.['200'] || [];
      const runners300m = data.Runners?.['300'] || [];

      if (runners200m.length === 0 && runners300m.length === 0) {
        toast.warning('No runners to download');
        return;
      }

      // Format data for Excel with the requested columns (removed bib number, start time, end time)
      const formatRunnerData = (runners, category) => {
        const formatValue = (value) => {
          if (
            value === null ||
            value === undefined ||
            value === '' ||
            (typeof value === 'number' && Number.isNaN(value)) ||
            (typeof value === 'string' && value.toLowerCase() === 'nan')
          ) {
            return '-';
          }
          return value;
        };

        const formatRunTime = (duration, cat) => {
          if (duration === 99.999) {
            return 'NA';
          }
          if (duration === null || duration === undefined || duration === '' || Number.isNaN(duration)) {
            return '';
          }
          const durationNum = typeof duration === 'number' ? duration : parseFloat(duration);
          if (Number.isNaN(durationNum)) {
            return '';
          }
          // 200m: show NA if time > 30s, 300m: show NA if time > 40s
          if (cat === '200m' && durationNum > 30) {
            return 'NA';
          }
          if (cat === '300m' && durationNum > 40) {
            return 'NA';
          }
          return duration;
        };

        const shouldShowNA = (duration, cat) => {
          if (duration === 99.999) {
            return true;
          }
          if (duration === null || duration === undefined || duration === '' || Number.isNaN(duration)) {
            return false;
          }
          const durationNum = typeof duration === 'number' ? duration : parseFloat(duration);
          if (Number.isNaN(durationNum)) {
            return false;
          }
          // 200m: show NA if time > 30s, 300m: show NA if time > 40s
          if (cat === '200m' && durationNum > 30) {
            return true;
          }
          if (cat === '300m' && durationNum > 40) {
            return true;
          }
          return false;
        };

        return runners.map((runner) => ({
          Rank: runner.Duration === 99.999 ? 'NC' : shouldShowNA(runner.Duration, category) ? 'NC' : runner.Rank || '',
          'வண்டி எண்': runner.CartNumber || '',
          'Token Number': runner.TokenNumber || '',
          'உரிமையாளர் 1': formatValue(runner.RunnerName),
          'உரிமையாளர் 2': formatValue(runner.RunnerName2),
          'Run Time': formatRunTime(runner.Duration, category),
          Category: category,
        }));
      };

      const data200m = formatRunnerData(runners200m, '200m');
      const data300m = formatRunnerData(runners300m, '300m');

      console.log('200m Data:', data200m);
      console.log('300m Data:', data300m);

      // Debug Tamil text specifically
      if (data200m.length > 0) {
        console.log('Sample Tamil text - 200m Runner Name 1:', data200m[0]['Runner Name 1']);
        console.log('Sample Tamil text - 200m Runner Name 2:', data200m[0]['Runner Name 2']);
      }
      if (data300m.length > 0) {
        console.log('Sample Tamil text - 300m Runner Name 1:', data300m[0]['Runner Name 1']);
        console.log('Sample Tamil text - 300m Runner Name 2:', data300m[0]['Runner Name 2']);
      }

      // Create a new workbook
      const workbook = XLSX.utils.book_new();

      // Helper function to style worksheet
      const styleWorksheet = (worksheet, data) => {
        // Get the range of the worksheet
        const range = XLSX.utils.decode_range(worksheet['!ref']);

        // Style headers (first row)
        for (let col = range.s.c; col <= range.e.c; col += 1) {
          const cellAddress = XLSX.utils.encode_cell({ r: 0, c: col });
          if (!worksheet[cellAddress]) worksheet[cellAddress] = { v: '' };
          worksheet[cellAddress].s = {
            font: { bold: true },
            alignment: { horizontal: 'center', vertical: 'center' },
            fill: { fgColor: { rgb: 'E6E6FA' } }, // Light purple background
          };
        }

        // Set column widths
        const columnWidths = [
          { wch: 8 }, // Rank
          { wch: 15 }, // வண்டி எண் (Cart Number)
          { wch: 35 }, // உரிமையாளர் 1 (Owner 1)
          { wch: 35 }, // உரிமையாளர் 2 (Owner 2)
          { wch: 15 }, // Run Time
          { wch: 12 }, // Category
        ];
        worksheet['!cols'] = columnWidths;

        // Center align all data cells
        for (let row = range.s.r; row <= range.e.r; row += 1) {
          for (let col = range.s.c; col <= range.e.c; col += 1) {
            const cellAddress = XLSX.utils.encode_cell({ r: row, c: col });
            if (!worksheet[cellAddress]) worksheet[cellAddress] = { v: '' };
            if (!worksheet[cellAddress].s) worksheet[cellAddress].s = {};
            worksheet[cellAddress].s.alignment = { horizontal: 'center', vertical: 'center' };
          }
        }

        return worksheet;
      };

      // Add 200m sheet if there are runners
      if (data200m.length > 0) {
        const worksheet200m = XLSX.utils.json_to_sheet(data200m);
        const styledWorksheet200m = styleWorksheet(worksheet200m, data200m);
        XLSX.utils.book_append_sheet(workbook, styledWorksheet200m, '200m Runners');
      }

      // Add 300m sheet if there are runners
      if (data300m.length > 0) {
        const worksheet300m = XLSX.utils.json_to_sheet(data300m);
        const styledWorksheet300m = styleWorksheet(worksheet300m, data300m);
        XLSX.utils.book_append_sheet(workbook, styledWorksheet300m, '300m Runners');
      }

      // Generate Excel file
      const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });

      // Create and download file
      const blob = new Blob([excelBuffer], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });
      const link = document.createElement('a');
      const downloadUrl = window.URL.createObjectURL(blob);
      link.setAttribute('href', downloadUrl);
      link.setAttribute('download', `${event.name || 'Event'}_runners.xlsx`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      // Clean up the URL object
      window.URL.revokeObjectURL(downloadUrl);

      toast.success('Runners list downloaded successfully');
    } catch (error) {
      console.error('Error downloading runners:', error);
      console.error('Error details:', error.message, error.stack);
      toast.error(`Error downloading runners list: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleNotComplete = async (runnerKey) => {
    try {
      console.log('Marking runner as foul/incomplete:', runnerKey);

      // Create FormData with Runner_key
      const formData = new FormData();
      formData.append('Runner_key', runnerKey);

      // API call to mark runner as foul using LiveTrack/MarkFoul endpoint
      const response = await fetch(`${URL}LiveTrack/MarkFoul`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
        },
        body: formData,
      });

      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }

      const data = await response.json();
      console.log('Mark foul response:', data);

      if (data.Status === 1) {
        toast.success('Runner marked as incomplete');
        // Refresh the runners list
        setRefresh((prev) => prev + 1);
      } else {
        toast.error(data.Message || 'Failed to mark runner as incomplete');
      }
    } catch (error) {
      console.error('Error marking runner as incomplete:', error);
      toast.error('Error marking runner as incomplete');
    }
  };

  // Helper function to extract time from datetime string
  const extractTimeFromDateTime = (dateTimeString) => {
    if (!dateTimeString) return '';
    // If it's already in HH:MM:SS format, return as is
    if (dateTimeString.match(/^\d{2}:\d{2}:\d{2}/)) {
      return dateTimeString.substring(0, 8); // Extract HH:MM:SS part
    }
    // If it's a full datetime string, extract time part
    const date = new Date(dateTimeString);
    if (Number.isNaN(date.getTime())) return '';
    return date.toTimeString().substring(0, 8); // Extract HH:MM:SS
  };

  const handleEditRunner = (runner) => {
    console.log('Edit runner:', runner);
    setEditingRunner(runner);
    setEditFormData({
      StartTime: extractTimeFromDateTime(runner.StartTime),
      EndTime: extractTimeFromDateTime(runner.EndTime),
      Duration: runner.Duration || '', // Pre-populate with existing duration data
    });
    setEditModalOpen(true);
  };

  const handleEditFormChange = (field, value) => {
    setEditFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  // Prevent navigation while uploading
  useEffect(() => {
    const handler = (e) => {
      if (batchRunning) {
        e.preventDefault();
        e.returnValue = '';
      }
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [batchRunning]);

  const appendBatchLog = useCallback((msg) => {
    setBatchLogs((prev) => [`${new Date().toLocaleTimeString()} - ${msg}`, ...prev].slice(0, 200));
  }, []);

  const handleBatchDetailsTabChange = useCallback((event, newValue) => {
    setBatchDetailsTab(newValue);
  }, []);

  // Extract bib number from filename (assumes filename without extension is the bib number)
  const extractBibFromFilename = useCallback((filename) => {
    // Remove path and extension, get just the base filename
    const baseName = filename.split('/').pop().split('\\').pop().replace(/\.[^/.]+$/, '');
    return baseName;
  }, []);

  // Send batch data to API after batch completion
  const sendBatchToAPI = useCallback(
    async (batchNumber, successfulUploads) => {
      if (successfulUploads.length === 0) {
        return;
      }

      const batchData = successfulUploads.map((upload) => ({
        bib: extractBibFromFilename(upload.filename),
        url: upload.final_url,
      }));

      const response = await fetch(SAVE_BATCH_ENDPOINT, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          event_id: eventId,
          data: batchData,
        }),
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`API call failed with status ${response.status}: ${errorText}`);
      }

      const result = await response.json();
      appendBatchLog(`Batch ${batchNumber} data saved to API successfully`);
      toast.success(`Batch ${batchNumber} completed successfully`);
    },
    [eventId, extractBibFromFilename, appendBatchLog, token]
  );

  const handleRetryFailedImage = useCallback(
    async (batchNumber, filename) => {
      try {
        const fileEntry = imageFiles.find((f) => f.filename === filename);
        if (!fileEntry) {
          toast.error('File not found for retry');
          return;
        }

        appendBatchLog(`Retrying upload for ${filename} (Batch ${batchNumber})`);

        const presignRes = await fetch(PRESIGN_ENDPOINT, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ event_id: eventId, filenames: [filename] }),
        });

        if (!presignRes.ok) {
          throw new Error(`Presign failed with status ${presignRes.status}`);
        }

        const presignJson = await presignRes.json();
        const urls = presignJson.urls || [];
        const presigned = urls.find((u) => u.filename === filename);
        if (!presigned) {
          throw new Error('No presigned URL returned for retry');
        }

        const result = await uploadSingleFileWithRetry(fileEntry, presigned);

        if (result.ok) {
          appendBatchLog(`Retry success for ${filename}`);
          setBatchStatus((prev) =>
            prev.map((b) => {
              if (b.batchNumber !== batchNumber) return b;
              const failed = (b.failed || []).filter((f) => f !== filename);
              const uploaded = b.uploaded.includes(filename) ? b.uploaded : [...b.uploaded, filename];
              return { ...b, failed, uploaded };
            })
          );
          setBatchStats((prev) => ({
            ...prev,
            uploaded: prev.uploaded + 1,
            failed: prev.failed > 0 ? prev.failed - 1 : 0,
          }));
          // Also update selectedBatchForView if it's the same batch
          setSelectedBatchForView((prev) => {
            if (!prev || prev.batchNumber !== batchNumber) return prev;
            const failed = (prev.failed || []).filter((f) => f !== filename);
            const uploaded = prev.uploaded.includes(filename) ? prev.uploaded : [...prev.uploaded, filename];
            return { ...prev, failed, uploaded };
          });
        } else {
          appendBatchLog(`Retry failed for ${filename}: ${result.error}`);
          toast.error(`Retry failed for ${filename}`);
        }
      } catch (error) {
        console.error('Retry upload error:', error);
        appendBatchLog(`Retry failed for ${filename}: ${error.message}`);
        toast.error(`Retry failed for ${filename}`);
      }
    },
    [appendBatchLog, eventId, imageFiles, token]
  );

  // Common function to process files (from folder or individual files)
  const processFiles = useCallback((files, isFolder = false) => {
    if (!files || files.length === 0) return;

    const mapped = files.map((f) => ({
      file: f,
      filename: isFolder && f.webkitRelativePath && f.webkitRelativePath.length ? f.webkitRelativePath : f.name,
      size: f.size,
    }));

    // Filter only image files
    const imageFilesOnly = mapped.filter((f) => {
      const ext = f.filename.split('.').pop().toLowerCase();
      return ['jpg', 'jpeg', 'png', 'gif', 'webp', 'bmp'].includes(ext);
    });

    if (imageFilesOnly.length === 0) {
      toast.warning(isFolder ? 'No image files found in the selected folder' : 'No image files selected');
      return;
    }

    // Merge with existing files if any
    const existingFiles = imageFiles.length > 0 ? imageFiles : [];
    const allFiles = [...existingFiles, ...imageFilesOnly];

    setImageFiles(allFiles);

    // Decide whether we can safely rebuild all batch status (no uploads started yet)
    const canRebuildCompletely =
      !existingFiles.length ||
      !batchStatus.length ||
      batchStatus.every(
        (b) =>
          (b.status === 'pending' || b.status === 'pending') &&
          (b.uploaded?.length || 0) === 0 &&
          (b.failed?.length || 0) === 0
      );

    if (canRebuildCompletely) {
      // Rebuild batches from scratch
      const allChunked = chunkArray(allFiles, BATCH_SIZE);
      setBatches(allChunked);
      setBatchStatus(
        allChunked.map((batch, idx) => ({
          batchNumber: idx + 1,
          uploaded: [],
          pending: batch.map((f) => f.filename),
          failed: [],
          status: 'pending',
        }))
      );
    } else if (existingFiles.length > 0 && batches.length > 0 && batchStatus.length > 0) {
      // Add new files to the last batch if it has space, otherwise create new batches
      const updatedBatches = [...batches];
      const updatedBatchStatus = [...batchStatus];

      // Get the last batch and check if it has space
      const lastBatchIndex = updatedBatches.length - 1;
      const lastBatch = updatedBatches[lastBatchIndex];
      const lastBatchStatus = updatedBatchStatus[lastBatchIndex];

      // Calculate how many files are already in the last batch (pending + uploaded + failed)
      const lastBatchFileCount = (lastBatchStatus.pending?.length || 0) +
        (lastBatchStatus.uploaded?.length || 0) +
        (lastBatchStatus.failed?.length || 0);

      const remainingSpace = BATCH_SIZE - lastBatchFileCount;

      if (remainingSpace > 0) {
        // Add files to the last batch
        const filesToAddToLastBatch = imageFilesOnly.slice(0, remainingSpace);
        const remainingFiles = imageFilesOnly.slice(remainingSpace);

        // Update last batch
        updatedBatches[lastBatchIndex] = [...lastBatch, ...filesToAddToLastBatch];
        updatedBatchStatus[lastBatchIndex] = {
          ...lastBatchStatus,
          pending: [...(lastBatchStatus.pending || []), ...filesToAddToLastBatch.map((f) => f.filename)],
        };

        // Create new batches for remaining files if any
        if (remainingFiles.length > 0) {
          const newBatches = chunkArray(remainingFiles, BATCH_SIZE);
          updatedBatches.push(...newBatches);

          const newBatchStatus = newBatches.map((batch, idx) => ({
            batchNumber: updatedBatches.length - newBatches.length + idx + 1,
            uploaded: [],
            pending: batch.map((f) => f.filename),
            failed: [],
            status: 'pending',
          }));
          updatedBatchStatus.push(...newBatchStatus);
        }
      } else {
        // Last batch is full, create new batches for all new files
        const newBatches = chunkArray(imageFilesOnly, BATCH_SIZE);
        updatedBatches.push(...newBatches);

        const newBatchStatus = newBatches.map((batch, idx) => ({
          batchNumber: updatedBatches.length - newBatches.length + idx + 1,
          uploaded: [],
          pending: batch.map((f) => f.filename),
          failed: [],
          status: 'pending',
        }));
        updatedBatchStatus.push(...newBatchStatus);
      }

      // Update batch numbers for all batches
      updatedBatchStatus.forEach((status, idx) => {
        status.batchNumber = idx + 1;
      });

      setBatches(updatedBatches);
      setBatchStatus(updatedBatchStatus);
    } else {
      // No existing batches, create new ones
      const allChunked = chunkArray(allFiles, BATCH_SIZE);
      setBatches(allChunked);
      setBatchStatus(
        allChunked.map((batch, idx) => ({
          batchNumber: idx + 1,
          uploaded: [],
          pending: batch.map((f) => f.filename),
          failed: [],
          status: 'pending',
        }))
      );
    }

    setBatchStats({
      uploaded: batchStats.uploaded,
      failed: batchStats.failed,
      total: allFiles.length,
    });
    setSelectedBatchForView(null);
    setCurrentBatchIndex(0);

    appendBatchLog(
      `Selected ${imageFilesOnly.length} ${isFolder ? 'images from folder' : 'images'}. Total: ${allFiles.length} images in ${allChunked.length} batches (${BATCH_SIZE} per batch).`
    );
    toast.success(`Selected ${imageFilesOnly.length} ${isFolder ? 'images from folder' : 'images'}. Total: ${allFiles.length} images in ${allChunked.length} batches`);
  }, [imageFiles, batchStatus, batchStats, appendBatchLog]);

  const handleImageSelect = (event) => {
    const files = Array.from(event.target.files || []);
    if (!files.length) return;
    processFiles(files, true);
    event.target.value = '';
  };

  const handleMultipleImageSelect = (event) => {
    const files = Array.from(event.target.files || []);
    if (!files.length) return;
    processFiles(files, false);
    event.target.value = '';
  };

  const handleDragDropFolder = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    if (batchRunning) return;

    const items = e.dataTransfer.items;
    const files = [];
    let hasDirectory = false;

    // Check if any item is a directory
    if (items) {
      for (let i = 0; i < items.length; i += 1) {
        const item = items[i];
        if (item.webkitGetAsEntry) {
          const entry = item.webkitGetAsEntry();
          if (entry && entry.isDirectory) {
            hasDirectory = true;
            // Read directory contents recursively
            const readDirectory = (dirEntry) => {
              const reader = dirEntry.createReader();
              reader.readEntries((entries) => {
                if (entries.length === 0) {
                  // All entries processed
                  if (files.length > 0) {
                    processFiles(files, true);
                  }
                  return;
                }
                entries.forEach((entry) => {
                  if (entry.isFile) {
                    entry.file((file) => {
                      files.push(file);
                    });
                  } else if (entry.isDirectory) {
                    readDirectory(entry);
                  }
                });
                readDirectory(dirEntry); // Continue reading
              });
            };
            readDirectory(entry);
            return;
          }
        }
      }
    }

    // If no directory found, treat as files or trigger folder selector
    if (!hasDirectory) {
      const droppedFiles = Array.from(e.dataTransfer.files || []);
      if (droppedFiles.length > 0) {
        // User dropped files on folder area - trigger folder selector instead
        const input = document.createElement('input');
        input.type = 'file';
        input.webkitdirectory = '';
        input.multiple = true;
        input.accept = 'image/*';
        input.onchange = (event) => {
          const selectedFiles = Array.from(event.target.files || []);
          if (selectedFiles.length > 0) {
            processFiles(selectedFiles, true);
          }
        };
        input.click();
      }
    }
  }, [batchRunning, processFiles]);

  const handleDragDropFiles = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    if (batchRunning) return;

    const files = Array.from(e.dataTransfer.files || []);
    if (files.length > 0) {
      processFiles(files, false);
    }
  }, [batchRunning, processFiles]);

  const clearBatchState = () => {
    setImageFiles([]);
    setBatches([]);
    setBatchStatus([]);
    setCurrentBatchIndex(0);
    setBatchRunning(false);
    setBatchStats({ uploaded: 0, failed: 0, total: 0 });
    setBatchLogs([]);
    setSelectedBatchForView(null);
    setBatchDetailsTab('uploaded');
  };

  // Wait for user decision on failed files
  const waitForFailedFilesDecision = () => {
    return new Promise((resolve) => {
      const checkDecision = setInterval(() => {
        if (!failedFilesModalOpen && pendingFailedFiles.length === 0) {
          clearInterval(checkDecision);
          resolve();
        }
      }, 100);
    });
  };

  const continueBatchProcessing = useCallback(async (batchIndex) => {
    if (batchIndex >= batches.length) {
      appendBatchLog(`Upload finished. Uploaded: ${batchStats.uploaded}, Failed: ${batchStats.failed}, Total: ${batchStats.total}`);
      setBatchRunning(false);
      return;
    }

    const batch = batches[batchIndex];
    const currentIndex = batchIndex;
    setCurrentBatchIndex(currentIndex + 1);
    appendBatchLog(`Processing batch ${currentIndex + 1}/${batches.length} (${batch.length} images)`);
    setBatchStatus((prev) =>
      prev.map((b, idx) => (idx === currentIndex ? { ...b, status: 'uploading' } : b))
    );

    try {
      const filenames = batch.map((b) => b.filename);
      const presignRes = await fetch(PRESIGN_ENDPOINT, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ event_id: eventId, filenames }),
      });
      if (!presignRes.ok) {
        throw new Error(`Presign failed with status ${presignRes.status}`);
      }
      const presignJson = await presignRes.json();
      const urls = presignJson.urls || [];
      const map = new Map(urls.map((u) => [u.filename, u]));

      let currentUploaded = 0;
      let currentFailed = 0;

      const results = await uploadBatchWithConcurrency(batch, map, (res) => {
        const progressKey = `${currentIndex}-${res.filename}`;
        setBatchUploadProgress((prev) => ({ ...prev, [progressKey]: res }));

        setBatchStatus((prev) =>
          prev.map((b, idx) => {
            if (idx !== currentIndex) return b;
            const pending = b.pending.filter((p) => p !== res.filename);
            const uploaded = res.ok ? [...b.uploaded, res.filename] : b.uploaded;
            const failed = res.ok ? b.failed : [...(b.failed || []), res.filename];
            return { ...b, pending, uploaded, failed };
          })
        );
        if (res.ok) {
          currentUploaded += 1;
          setBatchStats((prev) => ({ ...prev, uploaded: prev.uploaded + 1 }));
        } else {
          currentFailed += 1;
          setBatchStats((prev) => ({ ...prev, failed: prev.failed + 1 }));
        }
      });

      const batchFailed = results.filter((r) => !r.ok);
      const batchSuccessful = results.filter((r) => r.ok && r.final_url);

      if (batchFailed.length > 0) {
        // Show failed files modal and wait for user decision
        setPendingFailedFiles(batchFailed);
        setPendingBatchIndex(currentIndex);
        setFailedFilesModalOpen(true);
        appendBatchLog(`Batch ${currentIndex + 1} completed with ${batchFailed.length} failures. Waiting for user decision...`);
        setBatchStatus((prev) =>
          prev.map((b, idx) =>
            idx === currentIndex ? { ...b, status: 'completed-with-errors' } : b
          )
        );
        // Don't proceed to next batch - wait for user decision
      } else if (batchSuccessful.length > 0) {
        // All successful - send to API and wait for it to complete before proceeding
        try {
          await sendBatchToAPI(currentIndex + 1, batchSuccessful);
          appendBatchLog(`Batch ${currentIndex + 1} completed successfully.`);
          setBatchStatus((prev) =>
            prev.map((b, idx) =>
              idx === currentIndex ? { ...b, status: 'done' } : b
            )
          );
          await sleep(200);
          // Only proceed to next batch after API call succeeds
          await continueBatchProcessing(batchIndex + 1);
        } catch (error) {
          // If save-batch API fails, stop processing and mark batch as failed
          console.error('Error saving batch to API:', error);
          appendBatchLog(`Batch ${currentIndex + 1} upload completed but save-batch API failed: ${error.message}`);
          setBatchStatus((prev) =>
            prev.map((b, idx) =>
              idx === currentIndex ? { ...b, status: 'failed' } : b
            )
          );
          setBatchRunning(false);
          toast.error(`Batch ${currentIndex + 1} upload completed but failed to save. Processing stopped.`);
        }
      } else {
        // No successful uploads, just mark as done and continue
        appendBatchLog(`Batch ${currentIndex + 1} completed with no successful uploads.`);
        setBatchStatus((prev) =>
          prev.map((b, idx) =>
            idx === currentIndex ? { ...b, status: 'done' } : b
          )
        );
        await sleep(200);
        await continueBatchProcessing(batchIndex + 1);
      }
    } catch (error) {
      setBatchStats((prev) => ({ ...prev, failed: prev.failed + batch.length }));
      appendBatchLog(`Batch ${currentIndex + 1} failed: ${error.message}`);
      setBatchStatus((prev) =>
        prev.map((b, idx) => (idx === currentIndex ? { ...b, status: 'failed' } : b))
      );
      setBatchRunning(false);
    }
  }, [batches, eventId, imageFiles, sendBatchToAPI, appendBatchLog, batchStats, token]);

  const handleSkipFailedFiles = useCallback(async () => {
    if (pendingBatchIndex === null) return;

    const batch = batches[pendingBatchIndex];
    const currentBatchStatus = batchStatus[pendingBatchIndex];
    const successfulUploads = imageFiles
      .filter((f) => currentBatchStatus.uploaded.includes(f.filename))
      .map((f) => {
        const result = batchUploadProgress[`${pendingBatchIndex}-${f.filename}`];
        return result && result.ok ? { filename: f.filename, final_url: result.final_url } : null;
      })
      .filter(Boolean);

    // Send successful uploads to API and wait for it to complete
    if (successfulUploads.length > 0) {
      try {
        await sendBatchToAPI(pendingBatchIndex + 1, successfulUploads);
        // Mark batch as done (with skipped failures) only after API succeeds
        setBatchStatus((prev) =>
          prev.map((b, idx) =>
            idx === pendingBatchIndex ? { ...b, status: 'done', failed: [] } : b
          )
        );
        setFailedFilesModalOpen(false);
        setPendingFailedFiles([]);
        const nextIndex = pendingBatchIndex;
        setPendingBatchIndex(null);

        // Continue with next batch only after API call succeeds
        if (batchRunning) {
          await continueBatchProcessing(nextIndex + 1);
        }
      } catch (error) {
        console.error('Error saving batch to API:', error);
        appendBatchLog(`Failed to save batch ${pendingBatchIndex + 1} data to API: ${error.message}`);
        toast.error(`Failed to save batch ${pendingBatchIndex + 1} data. Processing stopped.`);
        setBatchStatus((prev) =>
          prev.map((b, idx) =>
            idx === pendingBatchIndex ? { ...b, status: 'failed' } : b
          )
        );
        setBatchRunning(false);
        setFailedFilesModalOpen(false);
        setPendingFailedFiles([]);
        setPendingBatchIndex(null);
      }
    } else {
      // No successful uploads, just mark as done and continue
      setBatchStatus((prev) =>
        prev.map((b, idx) =>
          idx === pendingBatchIndex ? { ...b, status: 'done', failed: [] } : b
        )
      );
      setFailedFilesModalOpen(false);
      setPendingFailedFiles([]);
      const nextIndex = pendingBatchIndex;
      setPendingBatchIndex(null);

      // Continue with next batch
      if (batchRunning) {
        await continueBatchProcessing(nextIndex + 1);
      }
    }
  }, [pendingBatchIndex, batches, batchStatus, imageFiles, batchUploadProgress, sendBatchToAPI, batchRunning, continueBatchProcessing, appendBatchLog]);

  const startBatchUpload = useCallback(async () => {
    if (!batches.length || batchRunning) return;
    setBatchRunning(true);
    appendBatchLog('Starting batch upload');
    setBatchUploadProgress({});
    await continueBatchProcessing(0);
  }, [batches.length, batchRunning, continueBatchProcessing, appendBatchLog]);

  const handleRetryFailedFilesInModal = useCallback(async () => {
    if (pendingBatchIndex === null) return;
    const failedFilenames = pendingFailedFiles.map((f) => f.filename);

    try {
      const presignRes = await fetch(PRESIGN_ENDPOINT, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ event_id: eventId, filenames: failedFilenames }),
      });

      if (!presignRes.ok) {
        throw new Error(`Presign failed with status ${presignRes.status}`);
      }

      const presignJson = await presignRes.json();
      const urls = presignJson.urls || [];
      const map = new Map(urls.map((u) => [u.filename, u]));

      const failedFiles = imageFiles.filter((f) => failedFilenames.includes(f.filename));
      const results = await uploadBatchWithConcurrency(failedFiles, map, (res) => {
        const progressKey = `${pendingBatchIndex}-${res.filename}`;
        setBatchUploadProgress((prev) => ({ ...prev, [progressKey]: res }));

        // Only update the specific failed image being retried
        setBatchStatus((prev) =>
          prev.map((b, idx) => {
            if (idx !== pendingBatchIndex) return b;
            // Only update failed and uploaded arrays, don't touch pending
            const uploaded = res.ok && !b.uploaded.includes(res.filename)
              ? [...b.uploaded, res.filename]
              : b.uploaded;
            const failed = res.ok && b.failed.includes(res.filename)
              ? b.failed.filter((f) => f !== res.filename)
              : b.failed;
            return { ...b, uploaded, failed };
          })
        );

        // Immediately remove successfully uploaded files from pendingFailedFiles
        if (res.ok) {
          setPendingFailedFiles((prev) => prev.filter((f) => f.filename !== res.filename));
          setBatchStats((prev) => ({ ...prev, uploaded: prev.uploaded + 1, failed: prev.failed - 1 }));
        }
      });

      const successful = results.filter((r) => r.ok && r.final_url);
      const stillFailed = results.filter((r) => !r.ok);

      if (stillFailed.length === 0) {
        // All retries successful - send to API and wait for it to complete
        if (successful.length > 0) {
          try {
            await sendBatchToAPI(pendingBatchIndex + 1, successful);
            setBatchStatus((prev) =>
              prev.map((b, idx) => (idx === pendingBatchIndex ? { ...b, status: 'done', failed: [] } : b))
            );
            appendBatchLog(`Batch ${pendingBatchIndex + 1} retry completed successfully.`);
            setFailedFilesModalOpen(false);
            setPendingFailedFiles([]);
            const nextBatchIndex = pendingBatchIndex;
            setPendingBatchIndex(null);

            // Continue with next batch only after API call succeeds
            if (batchRunning) {
              await continueBatchProcessing(nextBatchIndex + 1);
            }
          } catch (error) {
            console.error('Error saving batch to API:', error);
            appendBatchLog(`Batch ${pendingBatchIndex + 1} retry completed but save-batch API failed: ${error.message}`);
            toast.error(`Batch ${pendingBatchIndex + 1} retry completed but failed to save. Processing stopped.`);
            setBatchStatus((prev) =>
              prev.map((b, idx) => (idx === pendingBatchIndex ? { ...b, status: 'failed' } : b))
            );
            setBatchRunning(false);
            setFailedFilesModalOpen(false);
            setPendingFailedFiles([]);
            setPendingBatchIndex(null);
          }
        } else {
          // No successful uploads, just mark as done and continue
          setBatchStatus((prev) =>
            prev.map((b, idx) => (idx === pendingBatchIndex ? { ...b, status: 'done', failed: [] } : b))
          );
          appendBatchLog(`Batch ${pendingBatchIndex + 1} retry completed with no successful uploads.`);
          setFailedFilesModalOpen(false);
          setPendingFailedFiles([]);
          const nextBatchIndex = pendingBatchIndex;
          setPendingBatchIndex(null);

          // Continue with next batch
          if (batchRunning) {
            await continueBatchProcessing(nextBatchIndex + 1);
          }
        }
      } else if (successful.length > 0) {
        // Some files still failed - send successful ones to API first
        try {
          await sendBatchToAPI(pendingBatchIndex + 1, successful);
          setPendingFailedFiles(stillFailed);
          appendBatchLog(`Batch ${pendingBatchIndex + 1} retry: ${successful.length} succeeded, ${stillFailed.length} still failed.`);
          // Keep modal open for remaining failures
        } catch (error) {
          console.error('Error saving batch to API:', error);
          appendBatchLog(`Failed to save batch ${pendingBatchIndex + 1} data to API: ${error.message}`);
          toast.error(`Failed to save batch ${pendingBatchIndex + 1} data`);
          // Keep modal open with all failed files (including the ones that were successful in retry but API failed)
          setPendingFailedFiles([...stillFailed, ...successful.map((s) => ({ filename: s.filename, error: 'API save failed' }))]);
        }
      } else {
        setPendingFailedFiles(stillFailed);
        appendBatchLog(`Batch ${pendingBatchIndex + 1} retry completed with ${stillFailed.length} failures.`);
        // Keep modal open for remaining failures
      }
    } catch (error) {
      console.error('Retry failed files error:', error);
      appendBatchLog(`Retry failed: ${error.message}`);
      toast.error('Failed to retry uploads');
    }
  }, [pendingBatchIndex, pendingFailedFiles, eventId, imageFiles, sendBatchToAPI, appendBatchLog, batchRunning, continueBatchProcessing, token]);

  const handleRetryBatch = useCallback(async (batchNumber) => {
    const batchIndex = batchNumber - 1;
    const batch = batches[batchIndex];
    if (!batch) return;

    // Get failed filenames before updating status
    const currentBatchStatus = batchStatus[batchIndex];
    const failedFilenames = currentBatchStatus?.failed || [];

    if (failedFilenames.length === 0) {
      toast.info('No failed files to retry in this batch.');
      return;
    }

    // Set status to uploading but don't move failed files to pending
    setBatchStatus((prev) =>
      prev.map((b, idx) =>
        idx === batchIndex ? { ...b, status: 'uploading' } : b
      )
    );

    try {
      const presignRes = await fetch(PRESIGN_ENDPOINT, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ event_id: eventId, filenames: failedFilenames }),
      });

      if (!presignRes.ok) {
        throw new Error(`Presign failed with status ${presignRes.status}`);
      }

      const presignJson = await presignRes.json();
      const urls = presignJson.urls || [];
      const map = new Map(urls.map((u) => [u.filename, u]));

      const failedFiles = imageFiles.filter((f) => failedFilenames.includes(f.filename));
      const results = await uploadBatchWithConcurrency(failedFiles, map, (res) => {
        const progressKey = `${batchIndex}-${res.filename}`;
        setBatchUploadProgress((prev) => ({ ...prev, [progressKey]: res }));

        // Only update the specific failed image being retried
        setBatchStatus((prev) =>
          prev.map((b, idx) => {
            if (idx !== batchIndex) return b;
            // Only update failed and uploaded arrays, don't touch pending
            const uploaded = res.ok && !b.uploaded.includes(res.filename)
              ? [...b.uploaded, res.filename]
              : b.uploaded;
            const failed = res.ok && b.failed.includes(res.filename)
              ? b.failed.filter((f) => f !== res.filename)
              : b.failed;
            return { ...b, uploaded, failed };
          })
        );
        if (res.ok) {
          setBatchStats((prev) => ({ ...prev, uploaded: prev.uploaded + 1, failed: prev.failed - 1 }));
        }
      });

      const successful = results.filter((r) => r.ok && r.final_url);
      const stillFailed = results.filter((r) => !r.ok);

      if (successful.length > 0) {
        try {
          await sendBatchToAPI(batchNumber, successful);
          if (stillFailed.length === 0) {
            setBatchStatus((prev) =>
              prev.map((b, idx) => (idx === batchIndex ? { ...b, status: 'done' } : b))
            );
            appendBatchLog(`Batch ${batchNumber} retry completed successfully.`);
          } else {
            setBatchStatus((prev) =>
              prev.map((b, idx) => (idx === batchIndex ? { ...b, status: 'completed-with-errors' } : b))
            );
            appendBatchLog(`Batch ${batchNumber} retry completed with ${stillFailed.length} failures.`);
          }
        } catch (error) {
          console.error('Error saving batch to API:', error);
          appendBatchLog(`Batch ${batchNumber} retry completed but save-batch API failed: ${error.message}`);
          toast.error(`Batch ${batchNumber} retry completed but failed to save`);
          setBatchStatus((prev) =>
            prev.map((b, idx) => (idx === batchIndex ? { ...b, status: 'failed' } : b))
          );
        }
      } else if (stillFailed.length === 0) {
        // No successful uploads
        setBatchStatus((prev) =>
          prev.map((b, idx) => (idx === batchIndex ? { ...b, status: 'done' } : b))
        );
        appendBatchLog(`Batch ${batchNumber} retry completed with no successful uploads.`);
      } else {
        setBatchStatus((prev) =>
          prev.map((b, idx) => (idx === batchIndex ? { ...b, status: 'completed-with-errors' } : b))
        );
        appendBatchLog(`Batch ${batchNumber} retry completed with ${stillFailed.length} failures.`);
      }
    } catch (error) {
      console.error('Retry batch error:', error);
      appendBatchLog(`Retry failed for batch ${batchNumber}: ${error.message}`);
      setBatchStatus((prev) =>
        prev.map((b, idx) => (idx === batchIndex ? { ...b, status: 'completed-with-errors' } : b))
      );
    }
  }, [batches, batchStatus, imageFiles, eventId, sendBatchToAPI, appendBatchLog, token]);

  const handleUpdateRunnerTimes = async () => {
    if (!editingRunner) return;

    try {
      setEditLoading(true);
      console.log('Updating runner times:', editingRunner.RunnerKey, editFormData);

      const response = await fetch(`${URL}Runner/UpdateRunnerTimes`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          RunnerKey: editingRunner.RunnerKey,
          StartTime: editFormData.StartTime,
          EndTime: editFormData.EndTime,
          Duration: editFormData.Duration,
        }),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }

      const data = await response.json();
      console.log('Update runner times response:', data);

      if (data.Status === 1) {
        toast.success('Runner times updated successfully');
        setEditModalOpen(false);
        // Refresh the runners list while preserving search term and current page
        setRefresh((prev) => prev + 1);
      } else {
        toast.error(data.Message || 'Failed to update runner times');
      }
    } catch (error) {
      console.error('Error updating runner times:', error);
      toast.error('Error updating runner times');
    } finally {
      setEditLoading(false);
    }
  };

  const handleCloseEditModal = () => {
    setEditModalOpen(false);
    setEditingRunner(null);
    setEditFormData({
      StartTime: '',
      EndTime: '',
      Duration: '',
    });
  };

  // Memoized callback functions for table handlers
  const handlePageChange = useCallback((newPage) => {
    setPage(newPage);
  }, []);

  const handleEditRunnerCallback = useCallback((runner) => {
    handleEditRunner(runner);
  }, []);

  const handleNotCompleteCallback = useCallback((runnerKey) => {
    handleNotComplete(runnerKey);
  }, []);

  const handleDeleteRunnerCallback = useCallback((runnerKey) => {
    confirmDelete(runnerKey);
  }, []);

  // Optimized search handlers with focus management
  const handleCartSearchChangeCallback = useCallback(
    (value) => {
      // Update ref immediately without causing re-render
      cartSearchTermRef.current = value;
      // Track this as the last focused input
      lastFocusedInputRef.current = cartSearchInputRef.current;
      // Debounced API call - this is what triggers the actual search and state update
      debouncedCartSearch(value);
    },
    [debouncedCartSearch]
  );

  const handleTokenSearchChangeCallback = useCallback(
    (value) => {
      // Update ref immediately without causing re-render
      tokenSearchTermRef.current = value;
      // Track this as the last focused input
      lastFocusedInputRef.current = tokenSearchInputRef.current;
      // Debounced API call - this is what triggers the actual search and state update
      debouncedTokenSearch(value);
    },
    [debouncedTokenSearch]
  );

  const handleRegularSearchChangeCallback = useCallback(
    (value) => {
      // Update ref immediately without causing re-render
      searchTermRef.current = value;
      // Track this as the last focused input
      lastFocusedInputRef.current = regularSearchInputRef.current;
      // Debounced API call - this is what triggers the actual search and state update
      debouncedRegularSearch(value);
    },
    [debouncedRegularSearch]
  );

  const handleDownloadCallback = useCallback(() => {
    handleDownloadRunners();
  }, []);

  const handleDeleteAllCallback = useCallback(() => {
    setMenuAnchor(null);
    confirmDelete(null);
  }, []);

  const handleMenuOpen = useCallback((event) => {
    event?.stopPropagation?.();
    if (event?.currentTarget) {
      setMenuAnchor(event.currentTarget);
      const rect = event.currentTarget.getBoundingClientRect();
      setMenuPosition({
        top: rect.bottom + 4,
        left: rect.right , // 200px is the popover width
      });
    }
  }, []);

  const handleMenuClose = useCallback(() => {
    setMenuAnchor(null);
  }, []);

  const handleDeleteRunnersClick = useCallback(() => {
    setMenuAnchor(null);
    setDeleteRunnersConfirmOpen(true);
  }, []);

  const handleClearImagesClick = useCallback(() => {
    setMenuAnchor(null);
    setClearImagesConfirmOpen(true);
  }, []);

  const handleUploadImagesCallback = useCallback(() => {
    setBatchUploadModalOpen(true);
  }, []);

  const handleDownloadImagesCallback = useCallback(async () => {
    try {
      const response = await fetch(`${URL}Runner/DownloadEventRunnersImages?EventInfoKey=${eventId}`, {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.status === 403) {
        console.warn('Access forbidden: 403');
        localStorage.setItem('adminAuthenticated', false);
        navigation('/login');
        return;
      }

      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }

      // Get the JSON data from the response
      const data = await response.json();

      // Check if the response is successful
      if (data.Status !== 1) {
        toast.error('Failed to fetch runners data for download');
        return;
      }

      // Check if result array exists
      if (!data.result || !Array.isArray(data.result)) {
        toast.warning('No runners data available');
        return;
      }

      // Transform data to include only the required columns
      const excelData = data.result.map((runner) => ({
        'Bib Number': runner.BiBNumber || '',
        'Category': runner.Category || '',
        'Name': runner.Name || '',
        'Image URL': runner.ImageURLs || '',
      }));

      if (excelData.length === 0) {
        toast.warning('No runners to download');
        return;
      }

      // Create a new workbook
      const workbook = XLSX.utils.book_new();

      // Create worksheet from JSON data
      const worksheet = XLSX.utils.json_to_sheet(excelData);

      // Set column widths for better readability
      const columnWidths = [
        { wch: 12 }, // Bib Number
        { wch: 15 }, // Category
        { wch: 25 }, // Name
        { wch: 60 }, // Image URL (wider for URLs)
      ];
      worksheet['!cols'] = columnWidths;

      // Append worksheet to workbook
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Runners Images');

      // Generate Excel file buffer
      const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });

      // Create blob and download
      const blob = new Blob([excelBuffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `runners_images_${eventId}.xlsx`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      toast.success('Runners images list downloaded successfully');
    } catch (error) {
      console.error('Error downloading runners list:', error);
      toast.error('Failed to download runners list');
    }
  }, [eventId, token, navigation]);

  const handleDeleteEventRunners = useCallback(async () => {
    try {
      const formData = new FormData();
      formData.append('EventInfoKey', eventId);

      const response = await fetch(`${URL}Runner/DeleteEventRunners`, {
        method: 'POST',
        headers: {
          accept: 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: formData,
      });

      if (response.status === 403) {
        console.warn('Access forbidden: 403');
        localStorage.setItem('adminAuthenticated', false);
        navigation('/login');
        return;
      }

      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }

      const data = await response.json();
      if (data.Status === 1) {
        toast.success('Event runners deleted successfully');
        setRefresh((prev) => prev + 1);
      } else {
        toast.error(data.Message || 'Failed to delete event runners');
      }
    } catch (error) {
      console.error('Error deleting event runners:', error);
      toast.error('Error deleting event runners');
    } finally {
      setDeleteRunnersConfirmOpen(false);
    }
  }, [eventId, token, navigation]);

  const handleClearAllImages = useCallback(async () => {
    try {
      const formData = new FormData();
      formData.append('EventInfoKey', eventId);

      const response = await fetch(`${URL}Runner/ClearAllImages`, {
        method: 'POST',
        headers: {
          accept: 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: formData,
      });

      if (response.status === 403) {
        console.warn('Access forbidden: 403');
        localStorage.setItem('adminAuthenticated', false);
        navigation('/login');
        return;
      }

      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }

      const data = await response.json();
      if (data.Status === 1) {
        toast.success('All runner images cleared successfully');
        setRefresh((prev) => prev + 1);
      } else {
        toast.error(data.Message || 'Failed to clear runner images');
      }
    } catch (error) {
      console.error('Error clearing runner images:', error);
      toast.error('Error clearing runner images');
    } finally {
      setClearImagesConfirmOpen(false);
    }
  }, [eventId, token, navigation]);

  const handleVisitorCountRefreshCallback = useCallback(() => {
    fetchVisitorCount();
  }, []);

  const handleFileUploadRefreshCallback = useCallback(() => {
    setRefresh((prev) => prev + 1);
  }, []);

  const handleCloseBatchModal = useCallback(() => {
    // Check if all files are uploaded (no pending files in any batch)
    const hasPendingFiles = batchStatus.some((batch) => (batch.pending?.length || 0) > 0);

    // Only show confirmation if:
    // 1. Upload is currently running, OR
    // 2. There are pending files that haven't been uploaded yet
    if (batchRunning || hasPendingFiles) {
      setShowCloseConfirm(true);
    } else {
      // All files are uploaded (or no files selected), close directly without confirmation
      setBatchUploadModalOpen(false);
    }
  }, [batchRunning, batchStatus]);

  const handleConfirmCloseBatchModal = useCallback(() => {
    setShowCloseConfirm(false);
    setBatchUploadModalOpen(false);
    // If not running (just files selected), clear the batch state
    if (!batchRunning) {
      clearBatchState();
    }
    // Note: If upload is running, it will continue in the background, but the modal will be closed
  }, [batchRunning]);

  const handleCancelCloseBatchModal = useCallback(() => {
    setShowCloseConfirm(false);
  }, []);

  // Memoized header component
  const PageHeader = memo(({ eventName, isRekla, onMenuOpen, menuButtonRef }) => (
    <Box mb={4}>
      {/* Icon + Title Row */}
      <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
        <Box display="flex" alignItems="center">
          <Link to="/dashboard/event" style={{ textDecoration: 'none' }}>
            <IconButton sx={{ color: '#9f1d21', mr: 1 }}>
              <ArrowLeft />
            </IconButton>
          </Link>
          <Typography variant="h4" sx={{ fontWeight: 600 }}>
            Manage Runners: {eventName}
          </Typography>
        </Box>
        <IconButton
          ref={menuButtonRef}
          onClick={onMenuOpen}
          sx={{
            color: '#666',
            '&:hover': { backgroundColor: '#f5f5f5' },
          }}
        >
          <MoreVertIcon />
        </IconButton>
      </Box>

      {/* Subtitle */}
      <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
        Upload a CSV/Excel file with runner details or manage individual runners.
      </Typography>
    </Box>
  ));

  // Memoized visitor count component
  const VisitorCountCard = memo(({ visitorCount, visitorCountLoading, startTime, endTime, onRefresh }) => {
    const formatDateForAPI = (dateString) => {
      if (!dateString) return null;
      const date = new Date(dateString);
      return date.toISOString().split('T')[0];
    };

    if (!startTime || !endTime) return null;

    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          mb: 2,
          p: 3,
          backgroundColor: '#fafafa',
          borderRadius: 3,
          border: '1px solid #e0e0e0',
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
          maxWidth: 500,
        }}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
          <Typography
            variant="body2"
            sx={{
              color: '#666666',
              fontSize: '0.875rem',
              fontWeight: 400,
            }}
          >
            Event Visitors
          </Typography>
          {visitorCountLoading ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <CircularProgress size={16} />
              <Typography variant="h5" sx={{ fontWeight: 600, color: '#333333' }}>
                Loading...
              </Typography>
            </Box>
          ) : (
            <Typography
              variant="h5"
              sx={{
                fontWeight: 700,
                color: '#333333',
                fontSize: '1.5rem',
              }}
            >
              {visitorCount.toLocaleString()}
            </Typography>
          )}
          <Typography
            variant="body2"
            sx={{
              color: '#888888',
              fontSize: '0.8rem',
              fontWeight: 400,
            }}
          >
            From {formatDateForAPI(startTime)} to {formatDateForAPI(endTime)}
          </Typography>
        </Box>
        <Button
          variant="contained"
          sx={{
            backgroundColor: '#2196f3',
            borderRadius: 6,
            px: 3,
            py: 1,
            textTransform: 'none',
            fontWeight: 500,
            fontSize: '0.875rem',
            '&:hover': {
              backgroundColor: '#1976d2',
            },
          }}
          onClick={onRefresh}
          disabled={visitorCountLoading}
        >
          Refresh
        </Button>
      </Box>
    );
  });

  // Custom search input component that doesn't re-render on every keystroke
  const SearchInput = memo(
    forwardRef(({ label, value, onChange, placeholder, loading, sx }, ref) => {
      const inputRef = useRef(null);

      // Combine external ref with internal ref
      useEffect(() => {
        if (ref) {
          if (typeof ref === 'function') {
            ref(inputRef.current);
          } else {
            ref.current = inputRef.current;
          }
        }
      }, [ref]);

      // Only update the input value when the prop changes, not on every keystroke
      useEffect(() => {
        if (inputRef.current && inputRef.current.value !== value) {
          inputRef.current.value = value;
        }
      }, [value]);

      return (
        <TextField
          inputRef={inputRef}
          label={label}
          variant="outlined"
          size="small"
          defaultValue={value}
          onChange={(e) => onChange(e.target.value)}
          sx={sx}
          placeholder={placeholder}
          InputProps={{
            endAdornment: loading ? <CircularProgress size={16} /> : null,
          }}
        />
      );
    })
  );

  // Memoized search section component
  const SearchSection = memo(
    ({
      isRekla,
      cartSearchTerm,
      tokenSearchTerm,
      searchTerm,
      searchLoading,
      onCartSearchChange,
      onTokenSearchChange,
      onRegularSearchChange,
      onDownload,
      onUploadImages,
      onDownloadImages,
      cartSearchInputRef,
      tokenSearchInputRef,
      regularSearchInputRef,
    }) => (
      <Box display="flex" gap={2} mb={2} alignItems="center" flexWrap="wrap" position="relative">
        {isRekla ? (
          <>
            <SearchInput
              ref={cartSearchInputRef}
              label="Search by Cart Number"
              value={cartSearchTerm}
              onChange={onCartSearchChange}
              placeholder="Type to search..."
              loading={searchLoading && cartSearchTerm}
              sx={{ minWidth: 200 }}
            />
            <SearchInput
              ref={tokenSearchInputRef}
              label="Search by Token Number"
              value={tokenSearchTerm}
              onChange={onTokenSearchChange}
              placeholder="Type to search..."
              loading={searchLoading && tokenSearchTerm}
              sx={{ minWidth: 200 }}
            />
          </>
        ) : (
          <SearchInput
            ref={regularSearchInputRef}
            label="Search"
            value={searchTerm}
            onChange={onRegularSearchChange}
            placeholder="Type to search..."
            loading={searchLoading && searchTerm}
          />
        )}

        {isRekla ? (
          <Button
            variant="contained"
            onClick={onDownload}
            startIcon={<Download size={16} />}
            sx={{
              backgroundColor: '#28a745',
              '&:hover': { backgroundColor: '#218838' },
              color: '#fff',
            }}
          >
            Download
          </Button>
        ) : null}

        {!isRekla && onUploadImages && (
          <>
            <Button
              variant="contained"
              onClick={onDownloadImages}
              startIcon={<Download size={16} />}
              sx={{
                backgroundColor: '#fff',
                color: '#000',
                textTransform: 'none',
                border: '1px solid #e0e0e0',
                boxShadow: 'none',
                '&:hover': {
                  backgroundColor: '#fff',
                  boxShadow: 'none',
                },
              }}
            >
              Download List
            </Button>
            <Button
              variant="contained"
              onClick={onUploadImages}
              startIcon={<Upload size={16} />}
              sx={{
                backgroundColor: '#fff',
                color: '#000',
                textTransform: 'none',
                border: '1px solid #e0e0e0',
                boxShadow: 'none',
                '&:hover': {
                  backgroundColor: '#fff',
                  boxShadow: 'none',
                },
              }}
            >
              Upload Images
            </Button>
          </>
        )}

      </Box>
    )
  );

  // Memoized table component to prevent unnecessary re-renders
  const RunnersTable = memo(
    ({ runners, isRekla, totalPages, page, onPageChange, onEditRunner, onNotComplete, onDeleteRunner }) => {
      const nonReklaHeader = ['Runner Name', 'BIB Number', 'Category', 'Actions'];
      const reklaHeader = ['Cart Number', 'Owner Name 1', 'Owner Name 2', 'Token Number', 'Category', 'Actions'];
      const tableHeader = isRekla ? reklaHeader : nonReklaHeader;

      const CustomTableCell = (data) => {
        return data?.map((item, index) => {
          return <TableCell key={index}>{item}</TableCell>;
        });
      };

      if (runners.length === 0) {
        return (
          <Box textAlign="center" p={4} border={1} borderColor="divider" borderRadius={2}>
            <Typography>No runners added yet</Typography>
            <Typography variant="body2" color="text.secondary">
              Upload a file or add runners manually
            </Typography>
          </Box>
        );
      }

      return (
        <>
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  {tableHeader?.map((header) => {
                    return <TableCell key={header}>{header}</TableCell>;
                  })}
                </TableRow>
              </TableHead>
              <TableBody>
                {runners.map((runner) => (
                  <TableRow key={runner.id} hover sx={{ cursor: 'pointer' }}>
                    {isRekla
                      ? CustomTableCell([
                        runner.cartNumber,
                        runner.name,
                        runner.name2 === 'nan' || runner.name2 === null || runner.name2 === undefined
                          ? '-'
                          : runner.name2,
                        runner.bibNumber,
                        runner.category,
                      ])
                      : CustomTableCell([runner.name, runner.bibNumber, runner.category])}
                    <TableCell align="right">
                      <Box display="flex" gap={0.5} justifyContent="flex-end">
                        {isRekla ? (
                          <Button
                            variant="outlined"
                            size="small"
                            startIcon={<X size={12} />}
                            onClick={(e) => {
                              e.stopPropagation();
                              onNotComplete(runner?.RunnerKey);
                            }}
                            sx={{
                              color: '#ff9800',
                              borderColor: '#ff9800',
                              fontSize: '0.75rem',
                              minWidth: 'auto',
                              px: 1,
                              '&:hover': {
                                backgroundColor: '#fff3e0',
                                borderColor: '#f57c00',
                              },
                            }}
                          >
                            Incomplete
                          </Button>
                        ) : null}
                        {isRekla ? (
                          <Button
                            variant="outlined"
                            size="small"
                            startIcon={<Edit size={12} />}
                            onClick={(e) => {
                              e.stopPropagation();
                              onEditRunner(runner);
                            }}
                            sx={{
                              color: '#2196f3',
                              borderColor: '#2196f3',
                              fontSize: '0.75rem',
                              minWidth: 'auto',
                              px: 1,
                              '&:hover': {
                                backgroundColor: '#e3f2fd',
                                borderColor: '#1976d2',
                              },
                            }}
                          >
                            Edit
                          </Button>
                        ) : null}
                        <Button
                          variant="text"
                          size="small"
                          color="error"
                          onClick={(e) => {
                            e.stopPropagation();
                            onDeleteRunner(runner?.RunnerKey);
                          }}
                          sx={{
                            fontSize: '0.75rem',
                            minWidth: 'auto',
                            px: 1,
                          }}
                        >
                          Delete
                        </Button>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          <Stack spacing={2} alignItems="center" mt={2}>
            <Pagination
              count={totalPages}
              page={page}
              onChange={(e, value) => onPageChange(value)}
              sx={{
                '& .MuiPaginationItem-root': {
                  color: '#9f1d21',
                  borderColor: '#9f1d21',
                },
                '& .Mui-selected': {
                  backgroundColor: '#fff',
                  color: '#9f1d21',
                  '&:hover': {
                    backgroundColor: '#d14488',
                  },
                },
              }}
            />
          </Stack>
        </>
      );
    }
  );
  return (
    <Box p={2}>
      <Box component="main" flexGrow={1} mx="auto">
        {event ? (
          <>
            <PageHeader
              eventName={event.name}
              isRekla={isRekla}
              onMenuOpen={handleMenuOpen}
              menuButtonRef={menuButtonRef}
            />
            
            <Popover
              open={Boolean(menuAnchor)}
              onClose={handleMenuClose}
              anchorReference="anchorPosition"
              anchorPosition={menuPosition}
              transformOrigin={{
                vertical: 'top',
                horizontal: 'right',
              }}
              PaperProps={{
                sx: {
                  width: 200,
                  boxShadow: '0px 4px 6px rgba(0, 0, 0, 0.1)',
                  position: 'absolute',
                },
              }}
            >
              {!isRekla && (
                <MenuItem
                  onClick={handleClearImagesClick}
                  sx={{
                    color: '#ff9800',
                    '&:hover': { backgroundColor: '#fff3e0' },
                  }}
                >
                  Clear All Images
                </MenuItem>
              )}
              {!isRekla && (
                <MenuItem
                  onClick={handleDeleteRunnersClick}
                  sx={{
                    color: '#dc3545',
                    '&:hover': { backgroundColor: '#ffe0e0' },
                  }}
                >
                  Delete Event Runners
                </MenuItem>
              )}
              <MenuItem
                onClick={handleDeleteAllCallback}
                sx={{
                  color: '#ff4a5c',
                  '&:hover': { backgroundColor: '#ffe0e0' },
                }}
              >
                Delete All Timing Info
              </MenuItem>
            </Popover>
            
            {isRekla ? (
              <VisitorCountCard
                visitorCount={visitorCount}
                visitorCountLoading={visitorCountLoading}
                startTime={event.startTime}
                endTime={event.endTime}
                onRefresh={handleVisitorCountRefreshCallback}
              />
            ) : <></>}

            {loading ? (
              <Box display="flex" justifyContent="center" alignItems="center" py={10}>
                <CircularProgress />
              </Box>
            ) : (
              <>
                <FileUpload id={eventId} onRefresh={handleFileUploadRefreshCallback} isRekla={isRekla} />

                <br />
                <Box>
                  <div
                    style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}
                  >
                    <Typography variant="h6" gutterBottom>
                      Runners List
                    </Typography>

                    <SearchSection
                      isRekla={isRekla}
                      cartSearchTerm={cartSearchTerm}
                      tokenSearchTerm={tokenSearchTerm}
                      searchTerm={searchTerm}
                      searchLoading={searchLoading}
                      onCartSearchChange={handleCartSearchChangeCallback}
                      onTokenSearchChange={handleTokenSearchChangeCallback}
                      onRegularSearchChange={handleRegularSearchChangeCallback}
                      onDownload={handleDownloadCallback}
                      onUploadImages={handleUploadImagesCallback}
                      onDownloadImages={handleDownloadImagesCallback}
                      cartSearchInputRef={cartSearchInputRef}
                      tokenSearchInputRef={tokenSearchInputRef}
                      regularSearchInputRef={regularSearchInputRef}
                    />
                  </div>
                  <RunnersTable
                    runners={runners}
                    isRekla={isRekla}
                    totalPages={totalPages}
                    page={page}
                    onPageChange={handlePageChange}
                    onEditRunner={handleEditRunnerCallback}
                    onNotComplete={handleNotCompleteCallback}
                    onDeleteRunner={handleDeleteRunnerCallback}
                  />
                </Box>
              </>
            )}
          </>
        ) : (
          <Box textAlign="center" py={10}>
            <Typography variant="h5" gutterBottom>
              Event Not Found
            </Typography>
            <Typography color="text.secondary" mb={2}>
              We couldn't find the event you're looking for.
            </Typography>
            <Button variant="contained" component={Link} to="/admin/events">
              Go to Events
            </Button>
          </Box>
        )}
      </Box>
      <Dialog open={!!selectedBatchForView} onClose={() => setSelectedBatchForView(null)} maxWidth="sm" fullWidth>
        <DialogTitle>
          {selectedBatchForView ? `Batch ${selectedBatchForView.batchNumber} Details` : 'Batch Details'}
        </DialogTitle>
        <DialogContent dividers sx={{ pt: 1.5, height: '500px', display: 'flex', flexDirection: 'column' }}>
          <Tabs
            value={batchDetailsTab}
            onChange={handleBatchDetailsTabChange}
            indicatorColor="primary"
            textColor="primary"
            variant="fullWidth"
            sx={{
              mb: 2,
              borderRadius: 1.5,
              backgroundColor: '#f5f5f5',
              minHeight: 42,
              '& .MuiTab-root': {
                textTransform: 'none',
                fontSize: '0.875rem',
                minHeight: 42,
                fontWeight: 500,
              },
              '& .MuiTabs-indicator': {
                height: 3,
                borderRadius: 2,
                backgroundColor:
                  batchDetailsTab === 'pending'
                    ? '#fff8e1'
                    : batchDetailsTab === 'uploaded'
                      ? '#c8e6c9'
                      : batchDetailsTab === 'failed'
                        ? '#ffcdd2'
                        : '#2196f3',
              },
            }}
          >

            <Tab
              label={`Pending (${selectedBatchForView?.pending.length || 0})`}
              value="pending"
              sx={{
                color: '#f9a825',
                '&.Mui-selected': {
                  backgroundColor: '#fff8e1',
                  color: '#f57f17',
                },
              }}
            />
            <Tab
              label={`Uploaded (${selectedBatchForView?.uploaded.length || 0})`}
              value="uploaded"
              sx={{
                color: '#2e7d32',
                '&.Mui-selected': {
                  backgroundColor: '#c8e6c9',
                  color: '#1b5e20',
                },
              }}
            />
            <Tab
              label={`Failed (${selectedBatchForView?.failed?.length || 0})`}
              value="failed"
              sx={{
                color: '#c62828',
                '&.Mui-selected': {
                  backgroundColor: '#ffcdd2',
                  color: '#b71c1c',
                },
              }}
            />
          </Tabs>

          {batchDetailsTab === 'uploaded' && (
            <Box
              sx={{
                borderRadius: 2,
                p: 1.5,
                flex: 1,
                display: 'flex',
                flexDirection: 'column',
                minHeight: 0,
              }}
            >
              <Paper variant="outlined" sx={{ p: 1, flex: 1, overflow: 'auto', boxShadow: 'none', minHeight: 0 }}>
                {selectedBatchForView?.uploaded.length ? (
                  <Stack spacing={0.5}>
                    {selectedBatchForView.uploaded.map((name) => (
                      <Typography key={name} variant="body2">
                        {name}
                      </Typography>
                    ))}
                  </Stack>
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    No uploaded images.
                  </Typography>
                )}
              </Paper>
            </Box>
          )}

          {batchDetailsTab === 'pending' && (
            <Box
              sx={{
                borderRadius: 2,
                p: 1.5,
                flex: 1,
                display: 'flex',
                flexDirection: 'column',
                minHeight: 0,
              }}
            >
              <Paper variant="outlined" sx={{ p: 1, flex: 1, overflow: 'auto', boxShadow: 'none', minHeight: 0 }}>
                {selectedBatchForView?.pending.length ? (
                  <Stack spacing={0.5}>
                    {selectedBatchForView.pending.map((name) => (
                      <Typography key={name} variant="body2">
                        {name}
                      </Typography>
                    ))}
                  </Stack>
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    No pending images.
                  </Typography>
                )}
              </Paper>
            </Box>
          )}

          {batchDetailsTab === 'failed' && (
            <Box
              sx={{
                borderRadius: 2,
                p: 1.5,
                flex: 1,
                display: 'flex',
                flexDirection: 'column',
                minHeight: 0,
              }}
            >
              <Paper variant="outlined" sx={{ p: 1, flex: 1, overflow: 'auto', boxShadow: 'none', minHeight: 0 }}>
                {selectedBatchForView?.failed?.length ? (
                  <Stack spacing={0.5}>
                    {selectedBatchForView.failed.map((name) => (
                      <Box
                        key={name}
                        sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}
                      >
                        <Typography variant="body2">{name}</Typography>
                        <Button
                          size="small"
                          variant="outlined"
                          startIcon={<RefreshCw size={14} />}
                          onClick={() => handleRetryFailedImage(selectedBatchForView.batchNumber, name)}
                          sx={{
                            textTransform: 'none',
                            borderColor: '#ff9800',
                            color: '#ff9800',
                            fontSize: '0.75rem',
                            '&:hover': {
                              borderColor: '#f57c00',
                              backgroundColor: '#fff3e0',
                            },
                          }}
                        >
                          Retry
                        </Button>
                      </Box>
                    ))}
                  </Stack>
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    No failed images.
                  </Typography>
                )}
              </Paper>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          {selectedBatchForView?.failed?.length > 0 && (
            <Button
              variant="outlined"
              startIcon={<RefreshCw size={16} />}
              onClick={() => {
                handleRetryBatch(selectedBatchForView.batchNumber);
                setSelectedBatchForView(null);
              }}
              disabled={batchRunning}
              sx={{
                textTransform: 'none',
                borderColor: '#ff9800',
                color: '#ff9800',
                '&:hover': {
                  borderColor: '#f57c00',
                  backgroundColor: '#fff3e0',
                },
              }}
            >
              Retry Batch
            </Button>
          )}
          <Button
            onClick={() => setSelectedBatchForView(null)}
            sx={{
              color: '#757575',
              '&:hover': {
                backgroundColor: '#f5f5f5',
              },
            }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
      <ConfirmDialog
        open={confirmOpen}
        onConfirm={handleConfirmedDelete}
        onCancel={() => setConfirmOpen(false)}
        message="Are you sure you want to delete?"
      />

      <ConfirmDialog
        open={deleteRunnersConfirmOpen}
        onConfirm={handleDeleteEventRunners}
        onCancel={() => setDeleteRunnersConfirmOpen(false)}
        message="Are you sure you want to delete all event runners? This action cannot be undone."
      />

      <ConfirmDialog
        open={clearImagesConfirmOpen}
        onConfirm={handleClearAllImages}
        onCancel={() => setClearImagesConfirmOpen(false)}
        message="Are you sure you want to clear all runner images? This action cannot be undone."
      />

      {/* Edit Runner Times Modal */}
      <Dialog open={editModalOpen} onClose={handleCloseEditModal} maxWidth="sm" fullWidth>
        <DialogTitle>
          Edit Runner Times
          {editingRunner && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              {isRekla
                ? `Cart: ${editingRunner.cartNumber} - ${editingRunner.name}`
                : `${editingRunner.name} (BIB: ${editingRunner.bibNumber})`}
            </Typography>
          )}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <Stack spacing={3}>
              <TextField
                label="Start Time"
                type="time"
                value={editFormData.StartTime}
                onChange={(e) => handleEditFormChange('StartTime', e.target.value)}
                fullWidth
                InputLabelProps={{
                  shrink: true,
                }}
                helperText={editFormData.StartTime ? 'Existing start time loaded' : 'No start time recorded'}
              />
              <TextField
                label="End Time"
                type="time"
                value={editFormData.EndTime}
                onChange={(e) => handleEditFormChange('EndTime', e.target.value)}
                fullWidth
                InputLabelProps={{
                  shrink: true,
                }}
                helperText={editFormData.EndTime ? 'Existing end time loaded' : 'No end time recorded'}
              />
              <TextField
                label="Duration"
                value={editFormData.Duration}
                onChange={(e) => handleEditFormChange('Duration', e.target.value)}
                fullWidth
                placeholder="e.g., 02:30:45"
                helperText={
                  editFormData.Duration
                    ? 'Existing duration loaded'
                    : 'Enter the completed time manually - Format: HH:MM:SS'
                }
              />
            </Stack>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseEditModal} disabled={editLoading}>
            Cancel
          </Button>
          <Button
            onClick={handleUpdateRunnerTimes}
            variant="contained"
            disabled={editLoading}
            sx={{ backgroundColor: '#9f1d21', '&:hover': { backgroundColor: '#e3272c' } }}
          >
            {editLoading ? <CircularProgress size={20} /> : 'Update Times'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Batch Image Upload Modal - only for non-Rekla events */}
      {!isRekla && (
        <Dialog
          open={batchUploadModalOpen}
          onClose={handleCloseBatchModal}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>
            <Box display="flex" justifyContent="space-between" alignItems="center">
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                Batch Image Upload ({BATCH_SIZE} per batch)
              </Typography>
              <IconButton
                onClick={handleCloseBatchModal}
                sx={{
                  color: 'text.secondary',
                  '&:hover': {
                    color: 'text.primary',
                    backgroundColor: 'action.hover',
                  },
                }}
              >
                <CloseIcon />
              </IconButton>
            </Box>
          </DialogTitle>
          <DialogContent dividers>
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 3 }}>
                Upload Files
              </Typography>

              {/* Drag and Drop Area */}
              <Box mb={3}>
                <Paper
                  component="label"
                  htmlFor="files-input"
                  onDragOver={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    if (!batchRunning) {
                      e.currentTarget.style.backgroundColor = '#f5f5f5';
                      e.currentTarget.style.borderColor = '#2196f3';
                    }
                  }}
                  onDragLeave={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    e.currentTarget.style.backgroundColor = '#ffffff';
                    e.currentTarget.style.borderColor = '#d0d0d0';
                  }}
                  onDrop={handleDragDropFiles}
                  sx={{
                    border: '2px dashed #d0d0d0',
                    padding: 4,
                    textAlign: 'center',
                    cursor: batchRunning ? 'not-allowed' : 'pointer',
                    backgroundColor: '#ffffff',
                    borderRadius: 2,
                    transition: 'all 0.2s',
                    opacity: batchRunning ? 0.6 : 1,
                    minHeight: 180,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    '&:hover': {
                      backgroundColor: batchRunning ? '#ffffff' : '#fafafa',
                    },
                  }}
                >
                  <input
                    id="files-input"
                    type="file"
                    hidden
                    multiple
                    accept="image/*"
                    onChange={handleMultipleImageSelect}
                    disabled={batchRunning}
                  />
                  <Box
                    sx={{
                      width: 56,
                      height: 56,
                      borderRadius: '50%',
                      backgroundColor: '#e0e0e0',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      mb: 2,
                    }}
                  >
                    <Plus size={28} color="#666666" strokeWidth={2.5} />
                  </Box>
                  <Typography
                    variant="body1"
                    sx={{
                      color: '#666666',
                      fontSize: '0.9375rem',
                      fontWeight: 400,
                    }}
                  >
                    Drag & drop images or click to select multiple images
                  </Typography>
                </Paper>
              </Box>

              {/* Upload and Clear Buttons - Only show after files are selected */}
              {(batches.length > 0 || imageFiles.length > 0) && (
                <Box
                  display="flex"
                  gap={2}
                  alignItems="center"
                  flexWrap="wrap"
                  mb={3}
                  sx={{
                    justifyContent: { xs: 'center', sm: 'flex-start' },
                  }}
                >
                  <Button
                    variant="contained"
                    onClick={startBatchUpload}
                    disabled={!batches.length || batchRunning}
                    startIcon={
                      batchRunning ? (
                        <CircularProgress size={16} sx={{ color: 'inherit' }} />
                      ) : (
                        <Play size={18} />
                      )
                    }
                    sx={{
                      textTransform: 'none',
                      px: 3,
                      py: 1.5,
                      backgroundColor: '#28a745',
                      fontWeight: 600,
                      fontSize: '0.875rem',
                      boxShadow: '0 2px 4px rgba(40, 167, 69, 0.3)',
                      '&:hover': {
                        backgroundColor: '#218838',
                        boxShadow: '0 4px 8px rgba(40, 167, 69, 0.4)',
                      },
                      '&:disabled': {
                        backgroundColor: '#c8e6c9',
                        color: '#fff',
                      },
                    }}
                  >
                    {batchRunning ? 'Uploading...' : 'Start Upload'}
                  </Button>
                  <Button
                    variant="outlined"
                    onClick={clearBatchState}
                    disabled={batchRunning && batchStats.uploaded === 0}
                    startIcon={<Trash2 size={18} />}
                    sx={{
                      textTransform: 'none',
                      px: 3,
                      py: 1.5,
                      borderColor: '#dc3545',
                      color: '#dc3545',
                      fontWeight: 500,
                      fontSize: '0.875rem',
                      '&:hover': {
                        borderColor: '#c82333',
                        backgroundColor: '#f8d7da',
                      },
                      '&:disabled': {
                        borderColor: '#e0e0e0',
                        color: '#bdbdbd',
                      },
                    }}
                  >
                    Clear
                  </Button>
                </Box>
              )}

              {(batches.length > 0 || imageFiles.length > 0) && (
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} mb={2} alignItems="center">
                  <Typography variant="body2">
                    Batches: {batches.length} | Images: {batchStats.total} | Uploaded: {batchStats.uploaded} | Failed:{' '}
                    {batchStats.failed}
                  </Typography>
                  <Box flex={1}>
                    <LinearProgress
                      variant={batchStats.total ? 'determinate' : 'indeterminate'}
                      value={
                        batchStats.total ? Math.min(100, (batchStats.uploaded / batchStats.total) * 100) : 0
                      }
                    />
                  </Box>
                  {batchRunning && (
                    <Typography variant="body2" color="primary">
                      Uploading batch {currentBatchIndex}/{batches.length}
                    </Typography>
                  )}
                </Stack>
              )}

              <Stack spacing={1} mb={2}>
                {batchStatus.map((batch) => {
                  const total = batch.uploaded.length + batch.pending.length + (batch.failed?.length || 0);
                  const percentage = total > 0 ? Math.round(((batch.uploaded.length) / total) * 100) : 0;
                  const getStatusIcon = () => {
                    if (batch.status === 'done') return <CheckCircle size={20} color="#2e7d32" />;
                    if (batch.status === 'failed' || batch.status === 'completed-with-errors') return <XCircle size={20} color="#c62828" />;
                    if (batch.status === 'uploading') return <CircularProgress size={20} />;
                    return null;
                  };
                  const getStatusColor = () => {
                    if (batch.status === 'done') return '#2e7d32';
                    if (batch.status === 'failed' || batch.status === 'completed-with-errors') return '#c62828';
                    if (batch.status === 'uploading') return '#2196f3';
                    return '#f9a825';
                  };

                  return (
                    <Paper
                      key={batch.batchNumber}
                      variant="outlined"
                      sx={{
                        p: 1.5,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        borderLeft: `4px solid ${getStatusColor()}`,
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flex: 1 }}>
                        {getStatusIcon()}
                        <Box sx={{ flex: 1 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                            <Typography variant="subtitle2">Batch {batch.batchNumber}</Typography>
                            <Typography variant="body2" sx={{ color: getStatusColor(), fontWeight: 500 }}>
                              {percentage}%
                            </Typography>
                          </Box>
                          <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                            Uploaded: {batch.uploaded.length} | Pending: {batch.pending.length} | Failed: {batch.failed?.length || 0}
                          </Typography>
                          {batch.status === 'uploading' && (
                            <LinearProgress
                              variant="determinate"
                              value={percentage}
                              sx={{ mt: 0.5, height: 4, borderRadius: 2 }}
                            />
                          )}
                        </Box>
                      </Box>
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        {(batch.failed?.length > 0 && (batch.status === 'completed-with-errors' || batch.status === 'failed')) && (
                          <Button
                            size="small"
                            variant="outlined"
                            startIcon={<RefreshCw size={14} />}
                            onClick={() => handleRetryBatch(batch.batchNumber)}
                            disabled={batchRunning}
                            sx={{
                              textTransform: 'none',
                              borderColor: '#ff9800',
                              color: '#ff9800',
                              fontSize: '0.75rem',
                              '&:hover': {
                                borderColor: '#f57c00',
                                backgroundColor: '#fff3e0',
                              },
                            }}
                          >
                            Retry
                          </Button>
                        )}
                        <Button
                          size="small"
                          variant="text"
                          onClick={() => setSelectedBatchForView(batch)}
                          sx={{ textTransform: 'none', fontSize: '0.75rem' }}
                        >
                          View
                        </Button>
                      </Box>
                    </Paper>
                  );
                })}
                {!batchStatus.length && (
                  <Typography variant="body2" color="text.secondary">
                    No images selected yet. Drag & drop or click to select multiple images. Files will be automatically separated into batches of {BATCH_SIZE}.
                  </Typography>
                )}
              </Stack>

              {batchLogs.length > 0 && (
                <Box mt={2}>
                  <Typography variant="subtitle2" gutterBottom>
                    Upload Activity
                  </Typography>
                  <Paper
                    variant="outlined"
                    sx={{ maxHeight: 200, overflow: 'auto', p: 1, fontSize: '0.85rem', backgroundColor: '#fff' }}
                  >
                    <Stack spacing={0.5}>
                      {batchLogs.map((log, idx) => (
                        <Typography key={idx} variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                          {log}
                        </Typography>
                      ))}
                    </Stack>
                  </Paper>
                </Box>
              )}
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseBatchModal}>Close</Button>
          </DialogActions>
        </Dialog>
      )}

      {/* Confirmation Dialog for closing modal during upload or with selected files */}
      <Dialog open={showCloseConfirm} onClose={handleCancelCloseBatchModal}>
        <DialogTitle>{batchRunning ? 'Stop Image Upload?' : 'Abort Image Upload?'}</DialogTitle>
        <DialogContent>
          <Typography>
            {batchRunning
              ? 'The batch upload is currently in progress. Do you want to stop the image uploading? The upload will continue in the background.'
              : (
                <>
                  You have selected images but they have not been uploaded yet. Do you want to abort{' '}
                  <strong>without uploading</strong> the images?
                </>
              )}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleCancelCloseBatchModal}
            sx={{
              color: '#757575',
              backgroundColor: '#f5f5f5',
              '&:hover': {
                backgroundColor: '#e0e0e0',
              },
            }}
          >
            Cancel
          </Button>
          <Button onClick={handleConfirmCloseBatchModal} variant="contained" color="error">
            {batchRunning ? 'Stop Upload' : 'Abort'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Failed Files Modal */}
      <Dialog open={failedFilesModalOpen} onClose={() => { }} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Box display="flex" alignItems="center" gap={1}>
            <XCircle size={24} color="#c62828" />
            <Typography variant="h6">Failed Files - Batch {pendingBatchIndex !== null ? pendingBatchIndex + 1 : ''}</Typography>
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {pendingFailedFiles.length} file(s) failed to upload. Choose an action:
          </Typography>
          <Paper variant="outlined" sx={{ maxHeight: 300, overflow: 'auto', p: 1.5 }}>
            <Stack spacing={1}>
              {pendingFailedFiles.map((failedFile, idx) => (
                <Box
                  key={idx}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    p: 1,
                    backgroundColor: '#ffebee',
                    borderRadius: 1,
                  }}
                >
                  <Typography variant="body2" sx={{ flex: 1, wordBreak: 'break-word' }}>
                    {failedFile.filename}
                  </Typography>
                  {failedFile.error && (
                    <Typography variant="caption" color="error" sx={{ ml: 1, fontSize: '0.7rem' }}>
                      {failedFile.error}
                    </Typography>
                  )}
                </Box>
              ))}
            </Stack>
          </Paper>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleSkipFailedFiles}
            startIcon={<SkipForward size={16} />}
            sx={{
              textTransform: 'none',
              color: '#757575',
              '&:hover': {
                backgroundColor: '#f5f5f5',
              },
            }}
          >
            Skip & Continue
          </Button>
          <Button
            variant="contained"
            startIcon={<RefreshCw size={16} />}
            onClick={handleRetryFailedFilesInModal}
            sx={{
              textTransform: 'none',
              backgroundColor: '#ff9800',
              '&:hover': {
                backgroundColor: '#f57c00',
              },
            }}
          >
            Try Again
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

const FileUpload = memo(({ id, onRefresh, isRekla }) => {
  const token = localStorage.getItem('accessToken');
  const [file, setFile] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [selectedSheet, setSelectedSheet] = useState('');
  const [sheets, setSheets] = useState([]);
  const [columns, setColumns] = useState([]);
  const [loading, setLoading] = useState(false);
  const [workbook, setWorkbook] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [columnMapping, setColumnMapping] = useState({});
  const [invalidFields, setInvalidFields] = useState([]);

  const inputArray = isRekla
    ? ['Token No', 'Cart No', 'Owner Name 1', 'Owner Name 2', 'Category', 'Mobile Number', 'Email', 'TRP 1', 'TRP 2']
    : ['BibNumber', 'RunnerName', 'Category', 'MobileNumber', 'Email'];

  const parseCSV = (uploadedFile) => {
    Papa.parse(uploadedFile, {
      header: true,
      complete: (result) => {
        const cols = result.data[0] ? Object.keys(result.data[0]) : [];
        setColumns(cols);
        setSheets(['CSV Data']);
        setSelectedSheet('CSV Data');
        setIsModalOpen(true);
      },
      error: (error) => console.error('CSV Parse Error:', error),
    });
  };

  const parseExcel = (uploadedFile) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const data = new Uint8Array(e.target.result);
      const wb = XLSX.read(data, { type: 'array' });
      setWorkbook(wb);
      setSheets(wb.SheetNames);
      setSelectedSheet(wb.SheetNames[0]);
      const firstSheet = wb.Sheets[wb.SheetNames[0]];
      const cols = XLSX.utils.sheet_to_json(firstSheet, { header: 1 })[0];
      setColumns(cols);
      setIsModalOpen(true);
    };
    reader.readAsArrayBuffer(uploadedFile);
  };

  const handleFile = (uploadedFile) => {
    const extension = uploadedFile.name.split('.').pop().toLowerCase();
    setIsUploading(true);
    setFile(uploadedFile);

    if (extension === 'csv') {
      parseCSV(uploadedFile);
    } else if (['xls', 'xlsx'].includes(extension)) {
      parseExcel(uploadedFile);
    } else {
      console.error('Unsupported file type');
    }

    setIsUploading(false);
  };

  const onDrop = useCallback((acceptedFiles) => {
    if (acceptedFiles.length) handleFile(acceptedFiles[0]);
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: '.csv, .xls, .xlsx',
    multiple: false,
  });

  const handleMappingChange = (field, value) => {
    const updatedValue = value === 'None' ? '' : value;
    const newMapping = { ...columnMapping, [field]: updatedValue };

    // Check for duplicates
    const selectedValues = Object.entries(newMapping)
      .filter(([key]) => key !== field)
      .map(([, val]) => val);
    const isDuplicate = selectedValues.includes(value) && value !== '';

    // Update mapping
    setColumnMapping(newMapping);

    // Manage invalid field state
    setInvalidFields((prev) => (isDuplicate ? [...new Set([...prev, field])] : prev.filter((f) => f !== field)));
  };

  const handleSend = async () => {
    if (!file || !selectedSheet) return;
    const customColumnMapping = isRekla
      ? {
        BibNumber: columnMapping['Token No'],
        Category: columnMapping.Category,
        MobileNumber: columnMapping['Mobile Number'],
        Email: columnMapping.Email,
        RunnerName: columnMapping['Owner Name 1'],
        RunnerName2: columnMapping['Owner Name 2'],
        CartNumber: columnMapping['Cart No'],
        MembershipId1: columnMapping['TRP 1'],
        MembershipId2: columnMapping['TRP 2'],
      }
      : { ...columnMapping };
    const formData = new FormData();
    formData.append('EventDetailKey', id);
    formData.append('file', file);
    formData.append('sheet_name', selectedSheet);
    formData.append('column_mapping', JSON.stringify(customColumnMapping));

    setLoading(true);
    try {
      const response = await fetch(`${URL}Runner/UploadRunners`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
        },
        body: formData,
      });

      const result = await response.json();
      console.log('Upload response:', result);
      if (result.Status) {
        toast.success('File uploaded successfully');
        setIsModalOpen(false);
        onRefresh?.();
      }
    } catch (error) {
      console.error('Upload failed:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Stack gap={2} mt={3}>
      <Typography variant="h6">Upload CSV or Excel File</Typography>

      <Box>
        {isUploading ? (
          <CircularProgress />
        ) : (
          <Paper
            {...getRootProps()}
            sx={{
              border: '2px dashed grey',
              padding: 4,
              textAlign: 'center',
              cursor: 'pointer',
              backgroundColor: isDragActive ? '#f0f0f0' : '#fafafa',
            }}
          >
            <Typography>{isDragActive ? 'Drop file here...' : 'Drag & drop or click to select file'}</Typography>
            <input {...getInputProps()} />
          </Paper>
        )}
      </Box>

      <Modal open={isModalOpen} onClose={() => setIsModalOpen(false)}>
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            bgcolor: 'background.paper',
            p: 4,
            borderRadius: 2,
            width: 400,
          }}
        >
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6">Map Data Fields</Typography>
            <IconButton
              onClick={() => setIsModalOpen(false)}
              sx={{
                color: 'text.secondary',
                '&:hover': {
                  color: 'text.primary',
                  backgroundColor: 'action.hover',
                },
              }}
            >
              <CloseIcon />
            </IconButton>
          </Box>

          {sheets.length > 1 && (
            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel>Select Sheet</InputLabel>
              <Select
                value={selectedSheet}
                label="Select Sheet"
                onChange={(e) => {
                  const sheetName = e.target.value;
                  setSelectedSheet(sheetName);
                  const sheet = workbook.Sheets[sheetName];
                  const cols = XLSX.utils.sheet_to_json(sheet, { header: 1 })[0];
                  setColumns(cols);
                }}
              >
                {sheets.map((sheet) => (
                  <MenuItem key={sheet} value={sheet}>
                    {sheet}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}

          {inputArray.map((field) => {
            const isOptional = field === 'MobileNumber' || field === 'Mobile Number' || field === 'Email';
            const label = isOptional ? `${field} (Optional)` : field;

            return (
              <FormControl fullWidth key={field} sx={{ mb: 2 }}>
                <InputLabel>{label}</InputLabel>
                <Select
                  value={columnMapping[field] || ''}
                  label={label}
                  onChange={(e) => handleMappingChange(field, e.target.value)}
                >
                  {columns.map((col, index) => (
                    <MenuItem key={index} value={col}>
                      {col}
                    </MenuItem>
                  ))}
                  <MenuItem value="None">None</MenuItem>
                </Select>
                {invalidFields.includes(field) && (
                  <FormHelperText>This column is already selected for another field.</FormHelperText>
                )}
              </FormControl>
            );
          })}
          <Button
            variant="contained"
            color="primary"
            fullWidth
            onClick={handleSend}
            disabled={loading || Object.keys(columnMapping).length < (isRekla ? 6 : 3)}
          >
            Send
          </Button>
        </Box>
      </Modal>
    </Stack>
  );
});

export default AdminRunners;

const ConfirmDialog = ({ open, onConfirm, onCancel, message }) => {
  if (!open) return null;

  return (
    <div style={styles.overlay}>
      <div style={styles.modal}>
        <p>{message || 'Are you sure?'}</p>
        <div style={styles.buttons}>
          <button onClick={onCancel} style={styles.cancel}>
            Cancel
          </button>
          <button onClick={onConfirm} style={styles.confirm}>
            Delete
          </button>
        </div>
      </div>
    </div>
  );
};

const styles = {
  overlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0,0,0,0.5)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 9999,
  },
  modal: {
    background: '#fff',
    padding: '20px',
    borderRadius: '12px',
    width: '300px',
    textAlign: 'center',
  },
  buttons: {
    display: 'flex',
    justifyContent: 'space-around',
    marginTop: '20px',
  },
  cancel: {
    backgroundColor: '#ccc',
    padding: '8px 16px',
    borderRadius: '5px',
  },
  confirm: {
    backgroundColor: '#e53935',
    color: '#fff',
    padding: '8px 16px',
    borderRadius: '5px',
  },
};
